/**
 * The shapes core-api returns. Hand-written rather than generated: unlike the event contract in
 * `src/contracts/` (which is vendored from the `shared-contracts` repo and must never be
 * edited), these are this service's own REST surface, and there is no generator wired for it.
 * Keep in step with `services/core-api/src/schemas.py` in the platform repo.
 */

export const ROLES = ["superadmin", "admin", "supervisor", "rep"] as const;
export type Role = (typeof ROLES)[number];

/** Widest first — index order is the hierarchy, mirroring `rbac.py`. */
const RANK: Record<Role, number> = { rep: 0, supervisor: 1, admin: 2, superadmin: 3 };

export function atLeast(role: Role | undefined, minimum: Role): boolean {
  return role !== undefined && RANK[role] >= RANK[minimum];
}

/** Whether this role sees campaign and line names (ACL-652). A rep does not: they need Ready, Not
 *  ready and the breaks, and the system picks the campaign for them. */
export function seesCampaigns(role: Role | undefined): boolean {
  return role !== "rep";
}

/** Which roles this actor may hand out. Mirrors `rbac.can_assign_role`; the server enforces it
 *  regardless, so this exists to keep impossible options out of the picker rather than to
 *  secure anything. */
export function assignableRoles(actor: Role | undefined): Role[] {
  if (!actor) return [];
  if (actor === "superadmin") return [...ROLES];
  return ROLES.filter((r) => RANK[actor] > RANK[r]);
}

/** Whether this person changes their own phone — its extension and whether it records — from
 *  their account settings. Admins and superadmins only: below admin, an admin sets both on the
 *  person's user record. Mirrors the role floor on core-api's `PUT /auth/me/endpoint`, which is
 *  what actually refuses it. */
export function editsOwnPhone(role: Role | undefined): boolean {
  return atLeast(role, "admin");
}

export const ROLE_LABELS: Record<Role, string> = {
  superadmin: "Superadmin",
  admin: "Admin",
  supervisor: "Supervisor",
  rep: "Rep",
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  superadmin: "Every tenant. Manages tenants, domains, and all users and endpoints.",
  admin: "One tenant, every domain in it. Manages its people and the phones that come with them.",
  supervisor: "One domain. Reads its people and their live state; cannot manage identity.",
  rep: "Themselves and their phone.",
};

/** The admin who opened this session as somebody else, on `/auth/me` (ADR-040 in `platform`). */
export interface SignedInBy {
  id: number;
  email: string;
  display_name: string | null;
}

/** The longest display name a person may have (ADR-172 in `platform`): it is their phone's caller
 *  ID name and what a greeting says to a lead. A real name may be up to 200 characters. */
export const DISPLAY_NAME_MAX = 60;

export interface AppUser {
  id: number;
  email: string;
  /** Their real name, which an admin keeps on the account (ADR-172 in `platform`). Absent from a
   *  server before it. */
  name?: string | null;
  /** The name they go by: on their calls, the floor, the reports and their voicemail greetings.
   *  They may change it themselves. */
  display_name: string | null;
  /** Their profile picture (ADR-079 in `platform`), or `null` until they set one. A path under the
   *  API, fetched with the session (`useAvatarImage`). Every new picture gets a new path, so a
   *  cached one is never out of date. */
  avatar_url: string | null;
  role: Role;
  is_active: boolean;
  tenant_id: number | null;
  tenant_name: string | null;
  domain_id: number | null;
  domain_name: string | null;
  sip_endpoint_id: number | null;
  sip_endpoint_extension: string | null;
  sip_endpoint_domain_name: string | null;
  /** Every identifier the endpoint answers to: its auth id, and the `number-alias` it dials as
   *  when one is set. `sip_endpoint_extension` is the one to show; this is the set to match a
   *  call leg against (`counterparty` in `lib/calls.ts`). Empty when there is no phone. */
  sip_endpoint_numbers: string[];
  /** Whether their phone records its own leg. `null` means they have no phone — distinct from
   *  `false`, which is a phone deliberately opted out. */
  sip_endpoint_record_calls: boolean | null;
  /** Whether the endpoint is in the switch's directory at all. A disabled one cannot register,
   *  so the console shows the phone as unavailable rather than merely signed out. */
  sip_endpoint_enabled: boolean | null;
  /** Which of the tenant's voices speaks a voicemail greeting in their name (ADR-114 in `platform`):
   *  the female one or the male one. `null` is the tenant's default. Absent from a core-api that
   *  predates it. */
  voice_gender?: "female" | "male" | null;
  /** Their own voice (ADR-117 in `platform`), made from a recording of theirs, which speaks their
   *  greetings instead while they have one. Only they record it; an admin can delete it. */
  own_voice?: OwnVoice | null;
  last_login_at: string | null;
  created_at: string;
  /** Only on `/auth/me`: set when an admin opened this session with a sign-in link. */
  signed_in_by?: SignedInBy | null;
}

/** Exactly four digits — the rule `core-api`'s `provisioning.py` enforces for a person's
 *  extension. Deliberately narrower than what an endpoint nobody owns may be called: a device's
 *  id is not a number anyone dials. */
export const EXTENSION_RE = /^\d{4}$/;

export interface Tenant {
  id: number;
  name: string;
  slug: string;
  status: "active" | "suspended";
  created_at: string;
  updated_at: string;
  user_count: number;
}

export interface Domain {
  id: number;
  name: string;
  json_data: Record<string, unknown>;
  outbound_routing: OutboundRouting;
  created_at: string;
  endpoint_count: number;
  /** The trunks `outbound_routing` resolves to right now, in dial order — what the switch would
   *  actually use. Empty means no call can leave this domain: either nothing is configured at
   *  all, or every trunk the domain chose has since been disabled. */
  outbound_gateways: GatewaySummary[];
  /** Whether a recording made in this domain is turned into text when it lands. The firm's call,
   *  not the rep's: `record_calls` on a phone decides whether there is audio at all, and this
   *  only decides what happens to it. Turning it off stops new jobs and keeps the transcripts
   *  already written. */
  transcribe_recordings: boolean;
  /** How many days this domain keeps a call's recording and transcripts, counted from the call:
   *  null follows the platform's window, 0 keeps everything. The firm's call, set by an admin.
   *  A shorter number deletes the calls already stored (`lib/retention.ts`). */
  retention_days: number | null;
  /** The window in force: the domain's own, or the platform's. 0 = everything is kept. */
  effective_retention_days: number;
  /** The platform's window, which a domain with no number of its own follows. */
  platform_retention_days: number;
}

/** What a retention window of `days` would delete from a domain on the sweep's next run. */
export interface RetentionDue {
  days: number;
  recordings: number;
}

/**
 * Which trunks a domain's reps dial out on. Mirrors
 * `services/shared/telephony/outbound.py` — the module both core-api and the XML gateway read
 * this shape with.
 *
 * `auto` is the default: the first enabled trunk on the external profile, which is what every
 * domain did before this was configurable. `gateways` is the operator's own ordered list, tried
 * left to right, and it is strict — if every trunk in it is disabled or gone, calls are
 * announced and dropped rather than sent out on a carrier the domain did not choose.
 */
export const OUTBOUND_MODES = ["auto", "gateways"] as const;
export type OutboundMode = (typeof OUTBOUND_MODES)[number];

export interface OutboundRouting {
  mode: OutboundMode;
  /** Gateway ids, in failover order. Empty in `auto` mode. */
  gateway_ids: number[];
  /**
   * What a rep with no DID of their own presents on a PSTN call, or null for no default.
   *
   * Only the *fallback*: a rep with a number routed to their phone or their room calls out as
   * that number, decided by the switch's XML gateway rather than here (ADR-013). Kept in both
   * modes — which trunk carries the call and which number it presents are independent choices.
   */
  caller_id_number: string | null;
}

/** A trunk as it appears inside a domain's resolved outbound list. */
export interface GatewaySummary {
  id: number;
  uuid: string;
  name: string;
  enabled: boolean;
}

/**
 * A SIP trunk — the carrier a call leaves on. Never carries the carrier password: reading that
 * back is its own request, the same as an endpoint's SIP password.
 */
export interface Gateway {
  id: number;
  /** What FreeSWITCH knows the trunk by; it appears in dial strings and CDRs and never changes.
   *  `name` is the label people read. */
  uuid: string;
  name: string;
  description: string | null;
  username: string;
  realm: string;
  proxy: string;
  register: boolean;
  profile_name: string;
  /** Extra sofia gateway params (`caller-id-in-from`, `expire-seconds`, …). */
  params: Record<string, string>;
  enabled: boolean;
  created_at: string;
  /** Domains routing outbound calls through this trunk. Non-zero blocks deleting it. */
  domain_count: number;
}

export interface GatewaySecret {
  name: string;
  username: string;
  password: string;
}

export interface Endpoint {
  id: number;
  extension: string;
  domain_id: number;
  domain_name: string | null;
  display_name: string | null;
  caller_id_number: string | null;
  number_alias: string | null;
  enabled: boolean;
  /** Whether calls this phone places, or is rung for, are recorded. On by default. */
  record_calls: boolean;
  created_at: string;
  assigned_user_id: number | null;
  assigned_user_email: string | null;
  assigned_user_name: string | null;
  /** `null` means the live-state store was unreachable — "unknown", not "offline". */
  registered: boolean | null;
}

/**
 * One recorded call leg.
 *
 * `call_id` is the channel uuid — the same id a `Cdr` carries — which is how a recording is
 * matched to a row of call history without the calls API knowing recordings exist.
 *
 * `audio_url` is a path on core-api, not a bucket link, and it needs the session: the player
 * fetches it through the API client rather than putting it in an `<audio src>`, because an
 * `<audio>` element sends no Authorization header.
 */
export interface Recording {
  call_id: string;
  domain: string | null;
  endpoint_user: string | null;
  audio_url: string;
  mime: string;
  size_bytes: number;
  duration_ms: number | null;
  sample_rate: number | null;
  channels: number | null;
  fs_host: string | null;
  recorded_at: string | null;
  expires_at: string | null;
  direction: string | null;
  caller_number: string | null;
  caller_name: string | null;
  callee_number: string | null;
  callee_name: string | null;
  cdr_duration_seconds: number | null;
  /** The whole-file transcription job for this file, or null when none was asked for. */
  transcript_status: TranscriptStatus | null;
  /** Whether the call is on legal hold: nothing deletes its recording or its transcripts until
   *  an admin releases it, and `expires_at` is null while it is (`lib/recording-hold.ts`). */
  on_hold: boolean;
  held_at: string | null;
  /** The admin's reason and who placed the hold. Null for anyone below admin, whatever the row
   *  holds: a reason may name a legal matter. */
  hold_reason: string | null;
  held_by_name: string | null;
  /** One line about the file when the job failed. */
  transcript_error: string | null;
}

/**
 * Where the whole-file pass over a call's recording stands. `pending` covers queued and
 * decoding alike — the console has nothing different to say about the two. `failed` is about
 * the FILE (missing, undecodable, silence throughout), so asking again only helps once whatever
 * was wrong with it is not.
 */
export type TranscriptStatus = "pending" | "done" | "failed";

/** Which leg of the call a line's audio arrived on.
 *
 * Named for the CAPTURE and not for a role, because a rep-to-rep call has no answer to "which
 * one is the lead". `near` is the leg the recording or tap was taken on — FreeSWITCH writes that
 * channel's own read stream to channel 0, so the mapping is fixed — and `far` is the party it was
 * speaking to. `unknown` is an honest mono capture: one mixed track cannot be split by leg, and
 * guessing would be worse than saying so.
 */
export type TranscriptSpeaker = "near" | "far" | "unknown";

export interface TranscriptSegment {
  seq: number;
  text: string;
  /** Seconds from the start of the captured audio — so on a `recording` line it lines up with
   *  the recording's own playhead, which is what makes the line clickable. */
  start_seconds: number;
  end_seconds: number | null;
  speaker: TranscriptSpeaker;
}

/**
 * The stored transcript(s) of one call.
 *
 * **Two lists, never merged.** `live` is captured turn by turn during the call by the tap;
 * `recording` is a whole-file pass over the audio afterwards. They are independent passes over
 * the same conversation and can disagree — different segmentation, different words in the hard
 * places — and a reader who cannot tell which produced a line cannot judge it. Both may be
 * present, either may be empty.
 *
 * `recording_status` is the whole-file job's and says nothing about `live`: null means no pass
 * was asked for (the tenant does not transcribe, or there is no recording), which is not the same
 * as a pass that found nothing to say — that one is `failed`.
 */
export interface CallTranscript {
  call_id: string;
  live: TranscriptSegment[];
  recording: TranscriptSegment[];
  recording_status: TranscriptStatus | null;
  recording_error: string | null;
}

/** Which pass a stored transcript came from: the whole-file pass over the recording, or the
 *  tap while the call was on. */
export type TranscriptSource = "recording" | "live";

/** One of our phones on a call, and the person on it when there is one. */
export interface TranscriptRep {
  extension: string;
  name: string | null;
}

/**
 * One row of a contact's Transcripts tab (`GET /leads/{id}/transcripts`, ADR-081 in platform): one
 * pass over one of their calls. `id` + `source` name it, and are what `POST /transcripts/lines`
 * takes back — `id` is the uuid its lines are stored under, the recording's or the leg's, not a
 * CDR id.
 */
export interface TranscriptListItem {
  id: string;
  source: TranscriptSource;
  started_at: string | null;
  duration_seconds: number | null;
  /** Our phones on the call, in the order they joined it. */
  reps: TranscriptRep[];
  campaign_id: string | null;
  /** Null with a `campaign_id` when the campaign was deleted since. */
  campaign_name: string | null;
  line_count: number;
}

/** `POST /transcripts/lines`: the lines of one transcript, in the order they were spoken. */
export interface TranscriptLines {
  id: string;
  source: TranscriptSource;
  lines: TranscriptSegment[];
}

/**
 * What a call was about, written from its transcript by a language model (ACL-399).
 *
 * `model` and `source` are shown with it: a summary is only readable against the model that
 * wrote it and the pass it was written from, and a reader who cannot see either has to take it
 * on faith. Null from the API when nobody has asked for one, which is every call's ordinary
 * state rather than an error.
 */
export interface WrittenCallSummary {
  call_id: string;
  text: string;
  /** The transcript pass it was written from: `live` or `recording`. */
  source: TranscriptSource;
  model: string;
  created_at: string;
}

/** `POST /calls/{id}/transcribe`: the job is queued, not done. */
export interface TranscribeAccepted {
  call_id: string;
  status: "accepted";
}

export interface EndpointSecret {
  extension: string;
  domain_name: string;
  password: string;
}

export interface IceServer {
  urls: string | string[];
  username?: string | null;
  credential?: string | null;
}

/**
 * One person a rep can dial. Mirrors `ColleagueResponse`.
 *
 * Deliberately thin, and that is the server's decision showing through: `GET /phone/directory`
 * is the one directory route a **rep** may call, so it carries an extension, the name their
 * colleagues already see on an internal call, and whether the phone is online. No email, no
 * role, no domain — everyone on the list is in the caller's own realm by construction.
 */
export interface Colleague {
  extension: string;
  /** Null for an endpoint nobody holds — a shared handset, a door phone. Both are dialable. */
  name: string | null;
  /** `null` means the switch's live-state store was unreachable: "we don't know", which is not
   *  the same as offline and must not be shown as it. */
  registered: boolean | null;
}

/** `GET /phone/internal-transfer-numbers`: GoHighLevel's own numbers, each as its last ten digits. */
export interface InternalTransferNumbers {
  numbers: string[];
}

/**
 * One call the signed-in rep's phone did not take, as `GET /phone/missed` lists them (ADR-048 in
 * `platform`). Mirrors `MissedCallResponse`. Three records feed the list — a leg that rang the
 * phone, a stay the ACD kept for the rep, the caller's leg of a call on their direct number while
 * the phone was unregistered — and `id` is stable per record, so it is the list key.
 */
export interface MissedCall {
  id: string;
  /** When the call reached the rep, or was meant for them. */
  at: string;
  /** The caller's own channel where the platform knows it; on a plain leg, the leg's own uuid. */
  call_id: string | null;
  caller_number: string | null;
  caller_name: string | null;
  /** What happened on the rep's side: their phone rang and went unanswered; their phone was not
   *  registered; the ACD did not ring them (a break, signed out, on another call). */
  reason: "no_answer" | "offline" | "unavailable";
  /** How long their phone rang for this caller, every ring added up. Null when it never rang. */
  rang_seconds: number | null;
  /** The caller dialed the rep's own number: on a campaign (ADR-037) or a direct one (ADR-032). */
  own_number: boolean;
  did: string | null;
  queue_name: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  lead_id: string | null;
  /** What became of the caller, from the ACD's stay: CONNECTED, DROP, ABANDONED, TIMEOUT, REHOMED
   *  or REMOVED. Null for a call the ACD never saw, and for a stay still open. */
  outcome: string | null;
  /** Which way the call ran, where known: `inbound` for a caller, `outbound` for the dialer's
   *  call. An ABANDONED outcome is the caller giving up on one and the dialer on the other. */
  direction: string | null;
  /** The colleague who took the caller instead: their extension, and their name when known. */
  taken_by: string | null;
  taken_by_name: string | null;
}

/**
 * One inbound call the carrier sent us that never reached us (platform ADR-178), from
 * `GET /lost-calls`: nothing of ours saw it, or the carrier ended it as failed or within two
 * seconds. The carrier's log is read every few minutes, so a row appears minutes after the call.
 */
export interface LostCall {
  id: number;
  /** When the carrier started the call, and ended it. */
  at: string;
  ended_at: string | null;
  /** E.164, or `anonymous` for a caller who hid their number (`hidden`). */
  caller_number: string;
  hidden: boolean;
  /** The tenant's newest contact with the caller's number. */
  lead_id: string | null;
  lead_name: string | null;
  /** Our number that was called, and the line it is: its campaign, or the rep whose own number it is. */
  our_number: string;
  line_name: string | null;
  /** The campaign the number routes to, which a call back goes out through. Null for a rep's
   *  own number, whose call back is a plain call from the phone. */
  campaign_id: string | null;
  campaign_name: string | null;
  /** completed, failed, busy, no-answer or canceled. */
  carrier_status: string;
  carrier_seconds: number;
  /** Our switch has a leg for it: it took the call and the carrier still cut it short. */
  reached_switch: boolean;
  found_at: string;
  /** The first thing that happened with the caller since: we called them, or they called in and
   *  reached us. */
  follow_up: { kind: "called_back" | "called_in"; at: string } | null;
}

/** Whether the carrier check runs on this server, and when it last read the carrier's log. */
export interface LostCallCheck {
  on: boolean;
  every_seconds: number | null;
  checked_at: string | null;
  problem: string | null;
}

export interface LostCallsPage {
  items: LostCall[];
  total: number;
  check: LostCallCheck;
}

export interface SoftphoneCredentials {
  ws_url: string;
  login: string;
  password: string;
  sip_endpoint_id: number;
  domain: string;
  extension: string;
  number_alias: string | null;
  caller_name: string;
  caller_number: string;
  ice_servers: IceServer[];
}

/**
 * What `POST /phone/room/participants` answers with. Mirrors `RoomParticipantResponse`.
 *
 * The room is not sent up — the server always uses the caller's own — so the answer is where the
 * console learns which room the caller was handed to.
 */
export interface RoomParticipant {
  room: string;
  profile: string;
  destination: string;
  /** The rep's own leg, echoed back: the caller moved is the one bridged to it. */
  call_id: string;
  command_id: string;
}

/**
 * What a conference roster row *is*. Mirrors `LegKind` in core-api's `src/legs.py`, which holds
 * the reasoning; the values are the wire format and the two have to agree.
 *
 * `unknown` is a leg the server found and could not place. The console renders no badge for it —
 * a row labelled "Unknown" tells a rep less than a row with nothing on it. `monitor` is a supervisor
 * listening in on a campaign call, whose row the phone drops (ADR-041).
 */
export type LegKind = "lead" | "caller" | "rep" | "room" | "monitor" | "unknown";

/** One identified leg. Mirrors `RoomLeg`. */
export interface RoomLeg {
  /** The roster's own hash key: mod_conference sets it to the member's channel uuid. */
  channel_id: string;
  kind: LegKind;
  /** Absent where direction says nothing about the participant, or the switch reported neither. */
  direction: "inbound" | "outbound" | null;
}

/**
 * What `POST /phone/room/legs` answers with. Mirrors `RoomLegsResponse`.
 *
 * Ids that are missing were not in the live-channel index — a leg that has already hung up, or
 * an index that could not be reached. Neither is an error: the row stays unlabelled.
 */
export interface RoomLegs {
  legs: RoomLeg[];
}

/**
 * A call on a rep's own number, in a room of its own (ADR-032). Mirrors `RoomCallResponse`.
 *
 * `joined` is false for a leg that is still ringing for somebody else's call, and then
 * `invited_by` / `invite_mode` say whose call it is and why. `caller_held` is what the switch
 * last reported about the outside party.
 */
export interface RoomMember {
  channel_id: string;
  member_id: number | null;
  kind: LegKind;
  direction: "inbound" | "outbound" | null;
  held: boolean;
  is_self: boolean;
  /** The colleague's extension, for one of the firm's legs. */
  extension: string | null;
  /** The outside party's number, for the caller's leg. */
  number: string | null;
  name: string | null;
  invited_by: string | null;
  invite_mode: InviteMode | null;
}

export type InviteMode = "consult" | "add";

export interface RoomInvite {
  channel_id: string;
  /** A colleague's extension, or null for an outside number (ADR-065), which is `number`. */
  extension: string | null;
  name: string | null;
  number?: string | null;
  /** Set for a short while after the leg hung up without joining: the switch's cause, so the
   *  phone can say why (ADR-065). Such an invite is no longer ringing. */
  ended_cause?: string | null;
  ended_at?: string | null;
  invited_by: string | null;
  mode: InviteMode | null;
  since: string | null;
}

export interface RoomCall {
  /** Null for a live leg on no room call: the ordinary answer for a call that just rings. */
  room: string | null;
  call_id: string;
  joined: boolean;
  caller_held: boolean;
  members: RoomMember[];
  invites: RoomInvite[];
  invited_by: string | null;
  invited_by_name: string | null;
  invite_mode: InviteMode | null;
}

/** What `POST /phone/room/hold` and `/phone/room/consult/end` answer with. */
export interface RoomCommands {
  room: string;
  call_id: string;
  command_ids: string[];
}

/** What `POST /phone/room/outside` answers with: the newcomer's leg, named before it rings. */
export interface OutsideParticipantResult {
  room: string;
  call_id: string;
  channel_id: string;
  number: string;
  name: string | null;
  via: "direct" | "acd";
  command_id: string;
}

/** An outside number a tenant's reps bring into calls by name (ADR-065). */
export interface SpeedDial {
  id: number;
  tenant_id: number;
  label: string;
  /** E.164. */
  number: string;
  sort_order: number;
}

/** What `POST /phone/room/invite` answers with: the colleague's leg, named before it rings. */
export interface RoomInviteResult {
  room: string;
  call_id: string;
  channel_id: string;
  extension: string;
  mode: InviteMode;
  command_id: string;
}

/** How a briefing ends (ADR-032): the colleague takes the caller, all three talk, or the rep who
 *  asked takes the caller back. */
export type ConsultOutcome = "complete" | "join" | "cancel";

/**
 * What `POST /phone/transfers` answers with. Mirrors `WarmTransferResponse`.
 *
 * Only the two ids the console already had. The far parties' channel uuids — the pair the server
 * actually bridged — are deliberately not in the answer: an unseen channel id is what authorises
 * a console to act on a call, and handing back two the browser never held would give it a handle
 * on calls that are no longer its own.
 */
export interface WarmTransfer {
  /** The rep's leg carrying the party who was handed over. */
  from_call_id: string;
  /** The rep's leg carrying the party who received them. */
  to_call_id: string;
  /** Correlates with `command.result.*` on the bus; useful in a support ticket. */
  command_id: string;
}

/**
 * What `POST /phone/transfers/blind` answers with. Mirrors `BlindTransferResponse`.
 *
 * `destination` comes back because the server normalises it — a number typed `(310) 555-0199`
 * is dialled as `3105550199` — and `context` because it is the one part of a blind transfer the
 * console does not choose: the rep's own domain, which is what decides whose `1003` was meant.
 */
export interface BlindTransfer {
  /** The rep's own leg, echoed back: the caller sent away is the one bridged to it. */
  call_id: string;
  destination: string;
  context: string;
  /** Correlates with `command.result.*` on the bus; useful in a support ticket. */
  command_id: string;
}

/** What `POST /phone/room` answers with. Mirrors `ConferenceRoomResponse` in `schemas.py`. */
export interface ConferenceRoom {
  /** `<extension>-room` — the room the user's phone was just rung into. */
  room: string;
  /** The mod_conference profile, served by fs-xml-gateway. */
  profile: string;
  /** `<room>@<profile>` — what anyone else dials or is transferred to, to reach it. */
  destination: string;
  extension: string;
  domain: string;
  /** Correlates with `command.result.*` on the bus; useful in a support ticket. */
  command_id: string;
}

// --- the ACD: taking calls ---------------------------------------------------------------------
//
// Mirrors `AgentStateResponse`, `AgentLine` and `AgentActionResponse` in core-api's `schemas.py`,
// and the live record the event-tracker writes (`AgentStateRecord` in
// `services/shared/state/queues.py`), which `/acd/me` passes through as it is — hence camelCase.

/** A line the rep staffs, and the campaign that owns it. */
export interface AgentLine {
  id: number;
  name: string;
  purpose: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  /** The line's campaign answers the calls it sends without a click. */
  force_auto_answer: boolean;
  /** The line's campaign lets its reps choose My calls only (ADR-037). */
  own_calls_only_allowed?: boolean;
  /** The rep's own numbers on the line's campaign (ADR-037): where the campaign's routing keeps them
   *  to their own callers. */
  own_numbers?: string[];
  /** Why the dialer gave the rep none of the line's campaign's calls when it last looked, while
   *  they were Ready (platform ADR-179, ACL-568): `no_lead_due`, `cadences_paused`,
   *  `own_callers_only`, or what stops the campaign dialing (`no_trunk`, `no_caller_id`,
   *  `no_route`, `no_cadence`, `cadence_unreadable`). Null when it said nothing,
   *  and absent from an older core-api. */
  no_calls_reason?: string | null;
  /** With `no_lead_due`: when the campaign's next lead is due for the rep; null when none is
   *  within 30 days. */
  next_due_at?: string | null;
}

/** The event-tracker's record of a rep: what the ACD last did with them. */
export interface AgentStateRecord {
  user: string;
  domain: string;
  /** The effective state across every line: available, ringing, busy, disposition, wrapup,
   *  paused, dialing, busy_external or offline. */
  state: string;
  /** The lines the rep is signed in to. */
  queues: string[];
  queueStates: Record<string, string>;
  queue?: string | null;
  since?: string | null;
  /** When the rep last stopped talking with somebody, and when this record began (ACL-517): the
   *  live floor counts a dialing rep from these, since `since` starts again with every dial.
   *  Absent on a record from before. */
  lastCallEndedAt?: string | null;
  signedInAt?: string | null;
  /** The break reason, while paused. */
  code?: string | null;
  channelId?: string | null;
  callerChannelId?: string | null;
  sessionId?: string | null;
  leadId?: string | null;
  /** What the lookup said about the inbound caller on hand (ACL-284): `found`, `no_lead` (we have
   *  no record of the number), `unreadable_number` (no number to look up) or `lookup_failed` (the
   *  lookup could not answer). Absent when nobody asked. */
  lookupStatus?: string | null;
  kind?: string | null;
  handoffId?: string | null;
  /** The other party on the call on hand, as the ACD knows them: the client on a line's call, or
   *  whoever is on a call of the rep's own. Kept while the rep rings, talks or dials. */
  callerNumber?: string | null;
  callerName?: string | null;
  /** A consult handoff being briefed right now (ADR-027): the handoff, the other rep's extension,
   *  and which side of it this rep is. Cleared when the briefing ends. */
  consultHandoffId?: string | null;
  consultWith?: string | null;
  consultRole?: string | null;
  /** The calls the rep finished in each line since signing in to it, as the ACD counts them. */
  callsHandledByQueue?: Record<string, number>;
  /** The conference room a call the ACD connected lives in (ADR-029), once the rep has taken it. */
  room?: string | null;
  /** The caller is on the hold a rep put them on in that room. */
  callerHeld?: boolean;
  /** The lines where the rep takes only the calls meant for them: My calls only (ADR-037), or
   *  for their next call (ADR-070). */
  ownCallsOnly?: string[];
  /** Of those, the lines where it lasts only until the rep answers a call meant for them: the
   *  choice they made for their next call (ADR-070). */
  ownCallsNext?: string[];
  /** The rep the call on hand was meant for (`user@domain`): the caller dialed that rep's own
   *  number, or a colleague sent them to that rep by name. */
  meantFor?: string | null;
}

/** `GET /acd/me`: the caller's own lines, skills and live state. */
export interface AgentState {
  user: string;
  domain: string;
  /** `null` when the live-state store could not say. */
  registered: boolean | null;
  lines: AgentLine[];
  skills: Record<string, number>;
  /** The campaign numbers that ring this rep first (ADR-037), E.164. */
  numbers?: string[];
  /** A line the rep is signed in to answers its calls without a click. Which calls the phone
   *  answers is up to each call's own line (`AgentLine.force_auto_answer`). */
  force_auto_answer: boolean;
  /** `null` until the ACD has a record of them. */
  state: AgentStateRecord | null;
}

export type AgentAction = "login" | "logout" | "pause" | "unpause";

/** Who an unknown inbound caller is to us, as the rep judged on the call (ACL-285). */
export type CallerRelation = "potential_lead" | "family" | "friend_referral" | "other";

/** `POST /acd/calls/{call_id}/lead`: the call's stays and legs now name the contact (ACL-285). */
export interface CallLeadResponse {
  call_id: string;
  lead_id: string;
  sessions: number;
  cdrs: number;
}

/** `GET /acd/calls/{call_id}/caller-name`: the carrier's name for an unknown caller (ACL-286). */
export interface CallerNameResponse {
  call_id: string;
  name: string | null;
  source: "carrier" | null;
}

/** `POST /acd/calls/{call_id}/hold`: the hold is on its way to the ACD, not done. */
export interface CallHoldResponse {
  call_id: string;
  held: boolean;
}

/** A supervisor's leg into a call (ADR-029). `monitor_id` is that leg's channel uuid, which is
 *  also the call id the supervisor's phone rings with. */
export interface MonitorResponse {
  monitor_id: string;
  call_id: string | null;
  mode: string | null;
}

/** What `POST /acd/agent/{action}` answers: the command went out, not that it was applied. */
export interface AgentActionResponse {
  command: string;
  user: string;
  domain: string;
  queue: string | null;
}

/**
 * A setter's request to send their caller to a closer, and how far it has got (ADR-016). Mirrors
 * `HandoffResponse`. The note is the setter's words for the closer and never leaves Postgres.
 */
export interface Handoff {
  id: string;
  /** The caller's channel. */
  call_id: string;
  campaign_id: string | null;
  lead_id: string | null;
  /** The setter who sent it, by extension and realm. */
  from_user: string;
  from_domain: string;
  /** The line the caller waits on. */
  queue_name: string;
  required_skills: SkillRequirement[];
  target_user: string | null;
  target_domain: string | null;
  note: string | null;
  /** `requested`, `waiting`, `offered`, `consulting`, `connected`, `cancelled`, `abandoned` or
   *  `failed`. `consulting` and `cancelled` belong to a warm handoff (ADR-027). */
  status: string;
  to_user: string | null;
  to_domain: string | null;
  reason: string | null;
  requested_at: string;
  waiting_at: string | null;
  connected_at: string | null;
  ended_at: string | null;
  expired_at: string | null;
  wait_seconds: number | null;
}

/** One rep on the line a handoff of the rep's call would wait in (ADR-031). */
export interface HandoffCloser {
  /** What `POST /handoffs` names them by. */
  sip_endpoint_id: number;
  user: string;
  domain: string;
  display_name: string | null;
  /** slug → level, what the ACD matches against. */
  skills: Record<string, number>;
  /** The ACD's state of them on that line — the states of `AgentStateRecord` — or `signed_out`
   *  for a rep the line has no record of. */
  state: string;
  on_line: boolean;
  /** Keeps to My calls only on that line (ADR-037): reached by name, never as "any closer". */
  own_calls_only?: boolean;
}

export interface HandoffSkill {
  slug: string;
  name: string;
  category: string | null;
}

/** `GET /handoffs/closers`: who the caller can be sent to, and the skills to narrow them by. */
export interface HandoffClosers {
  queue_id: number;
  queue_name: string;
  campaign_id: string | null;
  /** The tenant's rep skills other than `closer`, which every handoff asks for. */
  skills: HandoffSkill[];
  closers: HandoffCloser[];
}

/** One caller's time on a line, as the event-tracker wrote it. Mirrors `QueueSessionResponse`
 *  (the fields the console reads); a rep reads only their own. */
export interface QueueSession {
  session_id: string;
  /** The caller's channel: what `POST /acd/calls/{id}/disposition` names. */
  channel_uuid: string;
  queue_name: string;
  campaign_id: string | null;
  lead_id: string | null;
  handoff_id: string | null;
  direction: string | null;
  kind: string | null;
  caller_number: string | null;
  caller_name: string | null;
  /** The rep whose own number the caller dialed (`user@domain`, ADR-037). */
  meant_for?: string | null;
  joined_at: string;
  exited_at: string | null;
  outcome: string | null;
  agent_user: string | null;
  agent_domain: string | null;
  bridge_started_at: string | null;
  bridge_ended_at: string | null;
  talk_seconds: number | null;
  disposition: string | null;
  disposition_at: string | null;
}

/** What `POST /acd/calls/{id}/disposition` answers: the outcome went out, as a 202. */
export interface CallDispositionResponse {
  call_id: string;
  session_id: string | null;
  disposition: string;
  lead_id: string | null;
  /** Whether the contract's `disposition.set` went out too; only a call with a lead has one. */
  contract_event: boolean;
  /** A compliance outcome marked the lead do-not-call. */
  dnc?: boolean;
  /** When the lead is called back, for a callback outcome on a call with a lead (ADR-080). */
  callback_at?: string | null;
  /** The backup time kept with that callback (ACL-559, ADR-183 in platform); null when none was
   *  given. Absent from an older core-api, which drops a backup time it is sent. */
  callback_backup_at?: string | null;
  /** Every backup time kept with it, in order (ADR-210 in platform). Absent from a core-api that
   *  keeps one, which saves the first alone. */
  callback_backup_times?: string[];
}

// --- the dialer on the rep's phone ---------------------------------------------------------------
//
// Mirrors `DialerOfferResponse`, `DialerCallResponse`, `OfferedLead`, `DialerActionResponse` and
// `VoicemailMessageResponse` in core-api's `schemas.py` (ADR-019).

/** A lead as the dialer offers it: what a rep reads before choosing Dial or Skip. */
export interface OfferedLead {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  state: string | null;
  language: string | null;
  timezone: string | null;
  /** The list's other columns, as imported. */
  attributes: Record<string, unknown> | null;
}

/** The lead the dialer offers this rep's phone in preview. */
export interface DialerOffer {
  /** What Dial and Skip name. */
  campaign_lead_id: number;
  campaign_id: string;
  campaign_name: string | null;
  offered_at: string | null;
  /** When the lead goes back to the pool unless the rep has dialed it. */
  expires_at: string | null;
  attempts: number;
  /** The slug of the outcome the lead's last call ended with. */
  last_disposition: string | null;
  lead: OfferedLead;
}

/**
 * `GET /dialer/current`: the lead the dialer is calling for this rep's phone, from the dial until
 * they are brought to the rep. Power mode offers nothing first, so this is where the phone learns
 * who it is calling. `attempts` and `last_disposition` are the list row's, as an offer's are: the
 * calls before this one.
 */
export interface DialerCall {
  call_id: string;
  stage: "dialing" | "ringing" | "answered";
  /** What answering machine detection does with this dial's answer (ADR-104, ADR-106 in
   *  `platform`): before the answer what the campaign would do, from the answer what the switch
   *  heard. Null when nothing listens, and absent from a core-api that does not say. */
  detection?: DialDetection | null;
  dial_mode: string;
  campaign_lead_id: number | null;
  campaign_id: string | null;
  campaign_name: string | null;
  attempts: number;
  last_disposition: string | null;
  started_at: string;
  lead: OfferedLead;
  /** Which attempt of its call the dial is (ADR-105 in `platform`): `n` is 0 for the call and k
   *  for its k-th redial, of `of` redials. Null on a call that carries none. This and the five
   *  below are absent from a core-api that predates them. */
  redial?: { n: number; of: number } | null;
  /** Which call of its round the dial is, counted from 1. Null for a number dialed by hand. */
  step_n?: number | null;
  /** The number the lead sees on this attempt, and what the cadence asked for and got:
   *  `branded`, `same_as_last`, `local`, `unbranded`, `rotate`, or a fallback written as
   *  `unbranded->branded`. */
  caller_id?: { number: string; strategy: string | null } | null;
  /** The attempts of this call made before this one, oldest first. Empty on the call itself. */
  earlier?: DialEarlierAttempt[];
  /** What the cadence does when a voicemail answers this attempt, whether or not anything
   *  listens to the answer. Null when no cadence call is behind the dial. */
  on_voicemail?: "hang_up" | "leave_message" | "leave_message_auto" | null;
  /** The message such an attempt leaves: the one the cadence names, else the lead's own. Null
   *  on an attempt that hangs up, and when the library has nothing to leave. */
  message_to_leave?: { slug: string; name: string } | null;
}

/** An attempt of the call being dialed that is already over: the call, or one of its redials. */
export interface DialEarlierAttempt {
  /** 0 the call, k its k-th redial. */
  n: number;
  /** How the switch said it ended: `no_answer`, `busy`, `voicemail`, `failed`... */
  outcome: string | null;
  /** The outcome recorded for it, by a rep or by the dialer: a slug of the campaign's set. */
  disposition: string | null;
  /** The number it presented. */
  number: string | null;
}

/** Detection on a dial the dialer is placing. `passive` false means it listens before it rings
 *  the rep: a person is brought to them, and a machine is hung up on. */
export interface DialDetection {
  mode: string;
  passive: boolean;
  /** human | machine | notsure; null until the switch has said. */
  result: string | null;
  /** What the cadence does on a voicemail at this attempt (ADR-107 in `platform`). On
   *  `leave_message` a campaign that listens first does not hang up on a machine: it brings it
   *  to the rep, who leaves the message. On `leave_message_auto` it leaves the message by itself
   *  and rings nobody (ADR-109). Absent from a core-api that predates it. */
  on_voicemail?: "hang_up" | "leave_message" | "leave_message_auto" | null;
}

/** How far a call the rep placed by hand through their campaign has got (ADR-047). */
export type ManualCallStage = "requested" | "dialing" | "ringing" | "answered" | "connected" | "ended";

/** `POST /dialer/calls` and `GET /dialer/calls/{id}`: a number the rep dialed by hand, placed the
 *  way the dialer places a preview dial, followed by the id core-api minted for it. */
export interface ManualCall {
  call_id: string;
  stage: ManualCallStage;
  phone: string;
  campaign_id: string;
  campaign_name: string | null;
  lead: OfferedLead;
  /** The number the call presents, once the dialer has decided it. */
  caller_id: string | null;
  /** `call.ended`'s outcome and the switch's hangup cause, once the call is over — or, for a call
   *  that never left, the dialer's own reason (`DNC`, `NO_TRUNK`, `NO_CALLER_ID`). */
  outcome: string | null;
  hangup_cause: string | null;
  started_at: string | null;
  answered_at: string | null;
  bridged_at: string | null;
  ended_at: string | null;
}

// --- a lead's card on the rep's phone --------------------------------------------------------------
//
// Mirrors `LeadCardResponse`, `LeadCardCampaign` and `LeadCardCall` in core-api's `schemas.py`
// (ADR-024).

/** The lead on one campaign's list. */
export interface LeadCardCampaign {
  campaign_id: string;
  campaign_name: string | null;
  state: string;
  attempts: number;
  /** How many calls the lead may get in all: the campaign's cadence's total attempts (ADR-067). */
  max_attempts?: number | null;
  /** The slug of the outcome the lead's last call on this list ended with. */
  last_disposition: string | null;
  last_attempt_at: string | null;
  next_attempt_at: string | null;
  /** The lead's hold, a trigger's (an appointment booked) or a hold disposition's: not dialed before it. */
  hold_until: string | null;
  hold_reason?: string | null;
  /** When the row leaves the list: the list horizon, or an exit (ADR-046). */
  retire_at?: string | null;
  list_id?: number | null;
  list_name?: string | null;
  /** The Dial column's word: the dialer's state, or `held` | `retired` | `on_call` | `needs_outcome` |
   *  `do_not_call` when core-api's columns or the lead say more (`contacts.dial_label` in core-api).
   *  `needs_outcome`: the call is over and the rep has not said what happened (ACL-486). */
  dial?: string | null;
  /** With `needs_outcome`, the rep whose outcome it waits for, by name; absent from an older core-api. */
  outcome_rep?: string | null;
  /** The earliest the row is dialable again; null when nothing keeps it. */
  next_at?: string | null;
}

/** A firm as a lead names one (ADR-045). */
export interface FirmRef {
  id: number;
  code: string;
  name: string;
  /** "[CA] Larry H. Parker" */
  label: string;
}

/** The newest note a person wrote on another record with the phone, on one line and cut for the card. */
export interface LeadSiblingNote {
  body: string;
  kind: "note" | "disposition" | "handoff";
  at: string;
}

/**
 * Another record with the same phone in the tenant (ACL-336, ADR-115 in the platform repo). It has the
 * same phone, and that is all it says: the same person or somebody else.
 */
export interface LeadSibling {
  id: string;
  stage: string | null;
  dnc: boolean;
  firm: FirmRef | null;
  created_at: string;
  /**
   * An admin deleted it. Listed because a deleted signed or do-not-call record still keeps the number
   * off the lists; nobody may open it. Absent from an older core-api.
   */
  deleted?: boolean;
  /** Whether it is the card's own person's (ADR-218), its accident's number (ADR-219) and whether it is
   *  still open: what a call needs to say which case it was about (ADR-223). Null while not known;
   *  absent from an older core-api. */
  same_person?: boolean | null;
  accident_number?: string | null;
  open?: boolean | null;
  /** The reader may open the record. The fields below are filled only then; absent from an older core-api. */
  may_open?: boolean;
  first_name?: string | null;
  last_name?: string | null;
  /** Its last outcome, on any campaign, and that outcome's name in the tenant's catalog. */
  disposition?: string | null;
  disposition_label?: string | null;
  /** A firm's reason for turning it down or replacing it, where a source wrote one. Null once it signed. */
  decline_reason?: string | null;
  last_note?: LeadSiblingNote | null;
}

/**
 * `GET /leads/history` (ACL-409, ADR-122 in the platform repo): the records a phone or an e-mail typed
 * into the new-contact form already has, before there is a lead, each as the card lists a record with
 * its phone. A record with both is listed once, under the phone.
 */
export interface LeadHistory {
  /** What was compared: the phone as stored (E.164), the e-mail trimmed and in lower case. */
  phone: string | null;
  email: string | null;
  /** The records of the tenant with the phone, newest first, a deleted one too: the newest 20. */
  same_phone: LeadSibling[];
  /** How many records have the phone in all. Absent from a core-api before it was counted. */
  same_phone_total?: number;
  /** The records with the e-mail on another phone, newest first, at most 20. */
  same_email: LeadSibling[];
}

/** A party's role in an accident (ACL-337, ADR-120 in the platform repo): the people and accidents
 *  model's words. */
export type AccidentRole = "driver" | "owner" | "passenger" | "pedestrian" | "guardian";
/** What a person said of two records. Nothing is said for them: until somebody does, it is `not_said`. */
export type AccidentRelation = "same_accident" | "another_accident" | "not_said";
/** Why a record is listed beside another: it has the phone, it has the e-mail, or a person linked it. */
export type AccidentWhy = "same_phone" | "same_email" | "linked";

/** The record's own place in an accident: which one, its role there, who said so and when. */
export interface AccidentOwn {
  /** The accident's id as text; `accident_id` is the same, and `number` is what a person says: `A-000123`
   *  (ADR-219 in the platform repo). */
  ref: string;
  accident_id: string;
  number: string;
  role: AccidentRole | null;
  linked_by: string | null;
  linked_at: string | null;
}

/** Another record listed beside this one (`GET /leads/{id}/accident`), saying why it is listed and how
 *  the two relate. The name, the outcome and who linked it are filled only when the reader may open it. */
/** Where a person's agreement stands (ACL-671, ADR-226 in the platform repo): none yet, sent and when,
 *  or signed and when the contract says. */
export interface AgreementState {
  state: "none" | "sent" | "signed";
  at: string | null;
}
/** The callback still to be made for a record listed beside this one (ACL-671). */
export interface AccidentRecordCallback {
  id: number;
  due_at: string;
  backup_at: string | null;
  status: "upcoming" | "due";
  rep: string | null;
  rep_user_id: number | null;
}
export interface AccidentRecord {
  id: string;
  /** Whose case the record is (ADR-218 in the platform repo), and whether that is this record's person:
   *  true, false, or null while either record's person is not known. Said by the people table, not by
   *  the phone: two people on one phone read false. */
  person_id: string | null;
  same_person: boolean | null;
  why: AccidentWhy;
  relation: AccidentRelation;
  deleted: boolean;
  may_open: boolean;
  stage: string | null;
  dnc: boolean;
  firm: FirmRef | null;
  created_at: string;
  role: AccidentRole | null;
  first_name: string | null;
  last_name: string | null;
  disposition: string | null;
  disposition_label: string | null;
  linked_by: string | null;
  linked_at: string | null;
  /** The campaigns whose lists the record is on now, for a reader who may open it (ADR-225): what
   *  "Add them to the dial list" starts from. Absent from an older core-api. */
  lists?: string[];
  /** Where the person stands, for the rep's screen (ACL-671): their number, their agreement, the callback
   *  still to be made for them, and whether a dial list has them. Filled only for a record the reader may
   *  open, like the name; absent from an older core-api. */
  phone?: string | null;
  agreement?: AgreementState | null;
  callback?: AccidentRecordCallback | null;
  on_list?: boolean | null;
}

/** One accident a person's cases are from (ADR-224), with how many of their shown cases it holds. */
export interface PersonAccident {
  id: string;
  number: string;
  created_at: string;
  cases: number;
}

/** A person and their cases (`GET /people/{id}`, ADR-224 in the platform repo): every case of theirs the
 *  reader may open, each as the card lists a record with the phone, newest first; how many they may not;
 *  and the accidents those cases are from. */
export interface Person {
  id: string;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  cases: LeadSibling[];
  cases_not_shown: number;
  accidents: PersonAccident[];
}

/** A contact's accident and the records listed beside it, from a route of its own (not the card). */
export interface LeadAccident {
  lead_id: string;
  /** This record's person (ADR-218), which each listed record's `same_person` is read against. */
  person_id?: string | null;
  accident: AccidentOwn | null;
  records: AccidentRecord[];
  /** This record's own agreement (ACL-671); absent from an older core-api. */
  agreement?: AgreementState | null;
}

/** One of the lead's calls: a stay in a line, a dial that never reached one, or a call only the
 *  switch recorded — a rep dialing out from their own phone, a caller put straight through on a
 *  rep's number (`direct`, ACL-496 in `platform`). */
export interface LeadCardCall {
  at: string;
  via: "queue" | "dialer" | "direct";
  campaign_id: string | null;
  campaign_name: string | null;
  direction: string | null;
  /** `inbound`, `outbound` or `handoff` for a queue call; `preview` or `power` for a dial. */
  kind: string | null;
  queue: string | null;
  /** The rep who took it, or was dialed for it (user@domain), and their account's name. */
  rep: string | null;
  rep_name: string | null;
  outcome: string | null;
  disposition: string | null;
  wait_seconds: number | null;
  talk_seconds: number | null;
  /** The lead leg's channel uuid: what `/cdrs?lead_id=`, the recording and the transcript are keyed on. */
  call_id?: string | null;
  has_recording?: boolean;
  has_transcript?: boolean;
  /** Which call of its cadence round a dialer call was, counted from 1, and which attempt of it
   *  (ADR-105 in `platform`): `redial_n` is 0 for the call and k for its k-th redial, of
   *  `redials_of`. `redial_n` is null on a call the dialer did not place as a cadence call, and
   *  `redials_of` on a dial made before it was recorded. Absent from an older core-api. */
  step_n?: number | null;
  redial_n?: number | null;
  redials_of?: number | null;
  /** The message that was left on this call's voicemail, by a rep or by the dialer, with the words
   *  of a greeting that was spoken (ADR-128 in `platform`). Null for a call that left none, and
   *  absent from an older core-api. */
  voicemail?: CallVoicemail | null;
  /** The callback this call was made for (ADR-162 in `platform`): the dialer placed it at the
   *  callback's time. Null for any other call; absent from an older core-api. */
  callback?: CallForCallback | null;
  /** The dialer hung up on the lead: they answered and no rep took the call in time. Its stay's
   *  outcome reads ABANDONED, as a caller's who gave up waiting does. Absent from an older core-api. */
  abandoned?: boolean;
}

/** The callback a call was made for, as its card names it. */
export interface CallForCallback {
  id: number;
  /** The rep it was kept for, and their account: a call another rep took was handed over. */
  rep: string | null;
  rep_user_id: number | null;
  purpose: string | null;
}

/** A lead's communication preference, `leads.contact_channel` (ACL-343): a preference, never a
 *  disposition, so it outlives every stage and outcome. `text_first` is texted first and called again
 *  once `text_first_until` passes; `sms_only` is never called by the dialer. */
export type ContactChannel = "calls_and_sms" | "text_first" | "sms_only";

/** `GET /leads/{id}`: a lead as the person working it sees it, and as the contact record shows it. */
/** A lead's preferred calling window: `HH:MM` on the lead's own clock, set by a person on the
 *  record (`source` is `person`; a callback sets none since ACL-661). */
export interface LeadCallingWindow {
  start: string;
  end: string;
  source: string;
  set_by_user_id: number | null;
  set_by_name: string | null;
  set_at: string | null;
}

export interface LeadCard {
  id: string;
  /** Whose case the record is (ADR-218 in the platform repo); null while not known. Absent from an older core-api. */
  person_id?: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  email?: string | null;
  state: string | null;
  language: string | null;
  /** IANA: the lead's own clock. */
  timezone: string | null;
  /** The clock the dialer calls them on: their own zone, else their state's, else the calling
   *  campaign's. A callback's time is picked on it (ADR-080). */
  clock_timezone?: string | null;
  source: string;
  source_ref?: string | null;
  /** Resolved from the firm tag the lead carries (ADR-045); null is "Needs firm". */
  firm?: FirmRef | null;
  stage: string | null;
  /** When the lead entered its stage; null when no history row says. */
  stage_since?: string | null;
  /** For a lead in an exit stage an admin may reopen (ADR-076): the stage a reopen restores unless
   *  the admin picks another. Null when there is nothing to reopen, or the lead is do-not-call. */
  reopen_stage?: string | null;
  /** The voicemail message this lead should hear, chosen server-side from their stage and
   *  language (ADR-061). The rep's phone leads with it; they may still pick another. */
  suggested_voicemail?: string | null;
  /** The lead's last disposition, on any campaign. */
  disposition: string | null;
  dnc: boolean;
  /** How the lead asked to be reached (ACL-343). Absent from an API that does not serve it yet. */
  contact_channel?: ContactChannel;
  /** When a Text first lead's automatic calls start; null for the other preferences. */
  text_first_until?: string | null;
  /** When the lead likes to be called, on their own clock (ADR-156 in platform); null for none,
   *  absent from an API that predates it. */
  calling_window?: LeadCallingWindow | null;
  /** How long a callback on them is worked after its time, from the cadence of the campaign whose
   *  list they are on (ACL-661). Null: just its call and redials. */
  callback_window_minutes?: number | null;
  consent_wording?: string | null;
  consent_captured_at: string | null;
  attributes: Record<string, unknown> | null;
  created_at: string;
  updated_at?: string | null;
  /** Where the lead is worked: the firm's dedicated campaign, else the tenant's calling one. */
  calling_campaign?: { id: string; name: string; slug: string } | null;
  campaigns: LeadCardCampaign[];
  siblings?: LeadSibling[];
  /** Newest first, at most ten. */
  calls: LeadCardCall[];
  /** The callback promised and not yet followed by an outcome, upcoming or due (ADR-080). */
  callback?: LeadCallback | null;
  /** Who the lead belongs to (ACL-408); null when nobody does. Absent from an older core-api. */
  owner?: LeadOwner | null;
}

// --- contacts (ADR-044) ----------------------------------------------------------------------------
//
// Mirrors `ContactRow`, `ContactSummary`, `LeadNoteResponse` and `LeadTimelineEntry` in core-api's
// `schemas.py`.

/** One line of `/contacts`: the person's stage beside one campaign's list row, never merged. */
export interface ContactRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  email: string | null;
  firm: FirmRef | null;
  state: string | null;
  language: string | null;
  timezone: string | null;
  stage: string | null;
  stage_since: string | null;
  /** The last outcome, on any campaign, and the dialer's time of the last attempt. */
  disposition: string | null;
  last_outcome_at: string | null;
  dnc: boolean;
  /** As on the card. Absent from an API that does not serve it yet. */
  contact_channel?: ContactChannel;
  text_first_until?: string | null;
  consent_captured_at: string | null;
  source: string;
  created_at: string;
  /** The calling campaign's list row, else the newest. */
  membership: LeadCardCampaign | null;
  dial: string | null;
  next_at: string | null;
  /** The newest of: a dial attempt, a note, a stage move. */
  last_activity_at: string | null;
}

// --- the board (ADR-078) -------------------------------------------------------------------------
//
// Mirrors `BoardCard`, `BoardColumn`, `ContactBoard`, `LeadStageMoveResponse` and
// `LeadDeleteResponse` in core-api's `schemas.py`.

/** A lead on the board: the list's row, and what its card shows besides. */
export interface BoardCard extends ContactRow {
  /** The CRM tags the lead arrived with, as they were written. */
  tags: string[];
  /** Notes on the record, of every kind. */
  notes: number;
  /** Dial attempts, over every list the lead is on. */
  attempts: number;
}

export interface BoardColumn {
  stage: string;
  /** Leads in the stage under the filter — the column's count, not the cards sent. */
  total: number;
  /** Of those, the ones the dialer would take now on some list. Absent from older APIs. */
  due_now?: number;
  /** Of those, the ones marked do-not-call. */
  dnc?: number;
  cards: BoardCard[];
}

export interface ContactBoard {
  columns: BoardColumn[];
  /** Leads with no stage yet: on no column. */
  no_stage: number;
}

export interface LeadStageMove {
  lead_id: string;
  stage: string;
  /** False when the lead was in that stage already (a scheduled stage rebooked). */
  moved: boolean;
  /** A scheduled stage's time: the dial lists wait until then. */
  until: string | null;
  lists_held: number;
  lists_released: number;
  /** List rows the dialer was asked to place for the new stage. */
  lists_updated: number;
}

export interface LeadDeleted {
  lead_id: string;
  deleted_at: string;
}

export interface ContactStageCount {
  stage: string;
  count: number;
  /** Leads of the stage with a list row the dialer would take now, but for the clock. */
  due_now: number;
}

/** `GET /leads/summary`: the tiles over the list, over the same filter minus the stage. */
export interface ContactSummary {
  total: number;
  by_stage: ContactStageCount[];
  no_stage: number;
  needs_firm: number;
  dnc: number;
  unassigned: number;
  exhausted: number;
  new_today: number;
  signed_this_month: number;
  due_now: number;
}

export type NoteKind = "note" | "disposition" | "handoff" | "system";

/** A note on a lead (`lead_notes`). */
export interface LeadNote {
  id: number;
  lead_id: string;
  kind: NoteKind;
  /** rep (a signed-in person, any role) | system | ghl | trigger (another system, through a trigger) */
  author: string;
  by_user_id: number | null;
  by: string | null;
  body: string;
  call_id: string | null;
  campaign_id: string | null;
  handoff_id: string | null;
  /** The outcome a `disposition` note came with. */
  disposition: string | null;
  created_at: string;
  updated_at: string;
  /** The workflow whose Add note step wrote it (ACL-370). Absent from an older core-api. */
  workflow?: SendingWorkflow | null;
}

export interface LeadStageChange {
  from_stage: string | null;
  to_stage: string;
  /** `disposition:<slug>` | `trigger:<id>` | `sibling:<lead id>` | `created` | `import:<id>` |
   *  `reopen: <reason>` | `stage_removed:<slug>` (ADR-077) | … */
  reason: string;
}

export interface LeadTimelineEvent {
  type: string;
  producer: string;
  payload: Record<string, unknown>;
}

export type TimelineKind = "call" | "note" | "stage" | "system" | "callback" | "message" | "owner" | "handoff";

/** Something the setter or the closer said about the lead in its conversation (platform ADR-189). */
export interface LeadHandoffMessage {
  id: number;
  sender_id: number | null;
  sender_name: string | null;
  body: string;
  created_at: string;
}

/** An ask for a closer about the lead, and what was said after it in the lead's conversation, up
 *  to the next ask (platform ADR-189). */
export interface LeadHandoffConversation {
  request_id: number;
  /** The lead's conversation, which the people in it open in Team Chat; null for an ask made
   *  before conversations were kept. */
  chat_id: number | null;
  state: CloserAsk["state"];
  asked_by: string | null;
  /** The team, or the one person, it was asked of. */
  asked_of: string | null;
  taken_by: string | null;
  /** The setter's note, as the closer got it. */
  note: string | null;
  /** Oldest first. */
  messages: LeadHandoffMessage[];
  /** The reader is in the conversation, so may answer it in Team Chat. */
  member: boolean;
}

/** The person a lead belongs to (ACL-408). */
export interface LeadOwner {
  id: number;
  name: string;
}

/** One change of a lead's owner (ACL-408), each side named as the person is now; null when nobody
 *  owned it, or the person was deleted. */
export interface LeadOwnerChange {
  from_user_id: number | null;
  from_name: string | null;
  to_user_id: number | null;
  to_name: string | null;
  reason: string;
}

// --- texts and emails (ADR-095, ADR-098, ADR-180 in platform) ----------------------------------------
//
// Mirrors `MessageResponse`, `ConversationResponse`, `ConversationMessages`, `InboxResponse`,
// `TextSend`, `TextCompose` and `TextNumber` in core-api's `schemas.py`, and for email
// `MessageDetail`, `EmailSend`, `ReplySend` and `EmailCompose`. Named Text… here: the
// Conversations screen has a model of its own for a thread of every kind of activity, and these are
// only what core-api keeps and serves. An email is a message of its own channel in the same tables.

/** Somebody on the team, named as the floor names people. */
export interface ConversationPerson {
  id: number;
  name: string;
}

/** Where a text stands. One we received is `received`. One we sent (ADR-098) is `queued` while it
 *  is ours, `sent` once the provider took it, then `delivered` or `failed`. */
/** `scheduled`: a text the system sent outside the person's hours, waiting for their 8 am (ACL-630). */
export type TextStatus = "received" | "scheduled" | "queued" | "sent" | "delivered" | "failed";

/** The workflow a text was sent by (ACL-385): its id and its name now. */
export interface SendingWorkflow {
  id: string;
  name: string;
}

/** The cadence step that sent a text (ADR-135 in platform): what the "Cadence" mark is drawn from. */
export interface MessageCadence {
  /** `slug@version`. */
  cadence: string;
  /** The cadence's name; null when the cadence is gone. */
  name: string | null;
  /** The round, and the step's place in it, from 1. */
  cycle: number;
  step: number;
}

/**
 * One picture or file a text carried (ADR-140).
 *
 * No address: a picture is fetched from `GET /conversations/{id}/media/{media_id}` with the
 * session's token, as a recording is, so `<img src>` cannot point at it directly.
 */
export interface TextMedia {
  id: string;
  /** `image` draws as a thumbnail; `file` as an icon and a name. */
  kind: "image" | "file";
  content_type: string;
  /** Null until the bytes are in the store. */
  byte_size: number | null;
  /** `pending` while it is being fetched, then `stored`, or `failed`. */
  state: "pending" | "stored" | "failed";
  /** Why it could not be kept: `type`, `too_large`, `provider`, `no_answer`, `no_store`. */
  error: string | null;
}

/** One text or email (ADR-095, ADR-180 in platform), as its conversation and the record's timeline
 *  show it. A list never carries a whole email: its `body` is "", and `GET /messages/{id}`
 *  (`MessageDetail`) is where the text and the HTML are read. */
export interface TextMessage {
  id: string;
  conversation_id: string;
  lead_id: string | null;
  /** sms | email */
  channel: "sms" | "email" | string;
  direction: "inbound" | "outbound";
  /** As on the wire: for a text we received, theirs then ours. For one we sent, ours then theirs. */
  from_number: string;
  to_number: string;
  body: string;
  /** How many pictures came with it. */
  media_count: number;
  /** The pictures themselves (ADR-140), in the order sent. Empty on a text that carried none,
   *  and on one received before pictures were kept. */
  media?: TextMedia[];
  status: TextStatus;
  /** When the status last changed. Null on a text we received. */
  status_at: string | null;
  /** Why a text failed: a code (`twilio:30007`, `no_answer`), and the same as a sentence. */
  error: string | null;
  error_detail: string | null;
  /** Who sent it. Null on a text we received. */
  sent_by: ConversationPerson | null;
  /** The cadence step that sent it; null on every other text, absent from a core-api that
   *  predates texts in a cadence (ADR-135). */
  sent_by_cadence?: MessageCadence | null;
  /** The workflow that sent it, for a text nobody sent by hand (ACL-385). Absent from an older
   *  core-api. */
  workflow?: SendingWorkflow | null;
  /** How many parts it travelled in. 0 on a text we received. */
  segments: number;
  /** The text was a STOP. */
  opt_out: boolean;
  at: string;
  /** A scheduled text: when it goes out, the person's 8 am (ACL-630). Null on every other, and
   *  absent from an older core-api. */
  send_at?: string | null;
  /** An email's subject. Null on a text, and absent from a core-api before ADR-180, as are the
   *  three below. */
  subject?: string | null;
  /** The short name an email goes by on its one-line summary: its subject, or "Automatic reply". */
  label?: string | null;
  /** An email's first words, at most 200, without the earlier mail it quotes. */
  preview?: string | null;
  /** An email that arrived: it came from the thread's address, which is still the lead's, and the
   *  sender's domain vouched for it. Null on a text and on an email we sent. */
  from_contact?: boolean | null;
}

/** `GET /messages/{id}`: one message whole. For an email, `body` is the whole text and `html` the
 *  HTML as it arrived, which is drawn only inside a sandboxed frame (`EmailDialog`). The lead's own
 *  address is "" for a viewer the lead form hides it from. */
export interface MessageDetail extends TextMessage {
  /** An email's HTML as received, never cleaned by core-api. Null on a text and on our own. */
  html: string | null;
  email_message_id: string | null;
  in_reply_to: string | null;
  references: string[];
  /** Every address it was sent to, for one that arrived. */
  email_to: string[];
  email_cc: string[];
  from_name: string | null;
  /** The thread's reply address. Filled for a supervisor and above only (support, local testing). */
  reply_address: string | null;
}

/** An email to a lead from their record (`POST /leads/{id}/email`). Without a subject core-api
 *  takes the thread's own ("Re: ..."), and refuses one that has none. */
export interface EmailSend {
  subject?: string;
  text: string;
}

/** A reply in a conversation (`POST /conversations/{id}/messages`), on whichever channel the
 *  conversation is: a subject only for an email, media only for a text. */
export interface ReplySend {
  text: string;
  subject?: string;
  media_ids?: string[];
}

/** Why an email may not leave: the `code` of a 409 from a send, and `EmailCompose.reason`. */
export type EmailRefusalCode =
  | "not_set_up" | "no_lead" | "address_changed" | "no_address" | "invalid_address" | "our_address"
  | "not_allowed" | "dnc" | "opted_out" | "daily_limit";

/** Whether an email may leave now, asked before anything is typed: `GET /leads/{id}/email/compose`
 *  and `GET /conversations/{id}/email/compose`. */
export interface EmailCompose {
  can_send: boolean;
  reason: EmailRefusalCode | null;
  /** The reason as a sentence, for the composer to show. */
  detail: string | null;
  /** Where it goes. Null when the lead form hides the address from the viewer: the server still
   *  knows it. */
  to_address: string | null;
  from_address: string | null;
  from_name: string | null;
  /** The thread an email would join, when the viewer may open it; null when it starts one, or joins
   *  one they cannot see (they are then given it). */
  conversation_id: string | null;
  /** The subject a reply takes by default ("Re: ..."), from a thread the viewer may open. */
  subject: string | null;
  max_chars: number;
  max_subject_chars: number;
}

/** A text to send (ADR-098). `from_number` is for a text from a lead's record only: one of the
 *  numbers `TextCompose.numbers` lists. Left out, the default. */
export interface TextSend {
  /** May be empty when `media_ids` is not: a picture with no caption is a legal MMS. */
  text: string;
  from_number?: string;
  /** Media already uploaded to the conversation's media route, at most ten (ADR-140). Absent
   *  against a core-api that predates outbound media. */
  media_ids?: string[];
}

/** Why a text may not leave: the `code` of a 409 from a send, and `TextCompose.reason`. */
export type TextRefusalCode =
  | "not_set_up" | "not_a_phone" | "dnc" | "opted_out" | "no_consent" | "no_number"
  | "number_cannot_text" | "no_timezone" | "quiet_hours" | "daily_limit" | "too_long"
  | "not_your_number"
  // Attachments (ADR-140): the composer shows core-api's `detail` for each rather than branching,
  // as none of them is a state the rep can be walked out of from here.
  | "media_not_ready" | "too_large" | "too_many" | "type" | "no_store" | "media_not_set_up";

/** One of our numbers a text to a lead can leave from. */
export interface TextNumber {
  number: string;
  /** thread: the lead and this number already have a conversation. campaign: a number of a campaign
   *  the lead is on. mine: the signed-in person's own number. */
  kind: "thread" | "campaign" | "mine";
  campaign_id: string | null;
  campaign_name: string | null;
  /** Whether the number is set up to send texts. */
  can_text: boolean;
  /** The one a text leaves from when none is named. */
  default: boolean;
}

/** Whether a text may leave now, asked before anything is typed: `GET /conversations/{id}/compose`
 *  and `GET /leads/{id}/sms/compose`. */
export interface TextCompose {
  can_send: boolean;
  reason: TextRefusalCode | null;
  /** The reason as a sentence, for the composer to show. */
  detail: string | null;
  /** For quiet_hours: when the person's 8 am comes round. */
  until: string | null;
  /** The number a text leaves from: the conversation's, or the lead's default. */
  from_number: string | null;
  /** A lead's composer only: every number the signed-in person may text them from. */
  numbers: TextNumber[];
  /** Set when this would be the first text to the number: the line added under it. */
  opt_out_line: string | null;
  max_chars: number;
}

/** Why Send contract cannot be pressed (ADR-134 in platform). `not_set_up`: the deployment has no
 *  contract service, and the record shows no button. `firm_inactive` (ACL-420): the firm is paused,
 *  not started or done. `missing_fields` (ACL-419): values every agreement needs are empty, and
 *  `fields` names them. */
export type ContractCheckReason = "not_set_up" | "no_firm" | "firm_inactive" | "missing_fields";

/** One of a lead's values, by its key and as the form labels it. */
export interface ContractField {
  key: string;
  label: string;
}

/** `GET /leads/{id}/contract`: whether Send contract can be pressed for the lead, and why not. */
export interface ContractCheck {
  can_send: boolean;
  reason: ContractCheckReason | null;
  /** The reason as a sentence. */
  detail: string | null;
  /** For `missing_fields` (ACL-419): the values every agreement needs that the lead has none for. */
  fields: ContractField[];
  /** The firm whose agreement goes out, as its label, once the lead has one (ACL-420). */
  firm: string | null;
}

/** One agreement Send contract offers to choose (ACL-578): the contract service's, of a firm that takes
 *  new clients. */
export interface ContractAgreement {
  /** Sent back as `agreement_id` when it is the one chosen. */
  id: number;
  /** As the contract service names it ("Larry H. Parker - Spanish Minor"). */
  name: string;
  /** The firm it is for: another than the contact's becomes theirs once it is sent. */
  firm_id: number;
  firm: string;
  /** Its version: Spanish or English, and whether a parent or guardian signs. */
  language: "en" | "es";
  is_minor: boolean;
  /** The contact's values it still needs (ACL-419): a guardian's too for a minor's agreement, and the
   *  firm's own required ones once the contract service lists them (ACL-604). */
  fields: ContractField[];
  /** Every value it is filled with (ACL-604), for the rep to check and fill in before the press: the
   *  contact's details, the contract's own, then the firm's. Absent from a core-api before ACL-604. */
  needs?: ContractNeed[];
}

/** One value an agreement is filled with (ACL-604): its key and label on the form, and whether the
 *  agreement cannot go out without it. The value itself is the record's, read through its form. */
export interface ContractNeed {
  key: string;
  label: string;
  required: boolean;
}

/** `GET /leads/{id}/contract/agreements`: what Send contract offers, the contact's own firm's first. */
export interface ContractAgreements {
  agreements: ContractAgreement[];
  /** The one the contract service would pick for the contact, preselected; null for none. */
  suggested: number | null;
  /** When none is suggested, why, as a sentence. */
  note: string | null;
}

/** `POST /leads/{id}/contract`: the contract service took the request. */
export interface ContractSent {
  /** sent: the agreement exists and the signing link went out. dry_run: everything was checked and
   *  the agreement chosen, but nothing was made or texted yet. */
  status: "sent" | "dry_run";
  /** The agreement that was chosen, as the contract service names it. */
  agreement: string;
  signing_url: string | null;
  submission_id: string | null;
  /** What goes on the agreement: its field name to the value. */
  fields: Record<string, string>;
  /** On `sent` (ACL-423): whether core-api texted the signing link to the lead, and if not, why
   *  not as a sentence. */
  texted: boolean;
  text_problem: string | null;
  /** Sent outside the lead's hours (ACL-630): the text is kept and goes out at this time, their
   *  8 am; `texted` is true. Null otherwise, and absent from an older core-api. */
  text_scheduled_for?: string | null;
  /** On `sent` (ACL-423): whether DocuSeal e-mailed its own invite, and to whom. The firm's
   *  automation decides. Optional: a core-api before ACL-423 does not say. */
  emailed?: boolean;
  email_to?: string | null;
}

/** What a press of Send contract came to: `sent`, `dry_run` (live sending off) or `failed`, and a
 *  sent one then `viewed`, and one of `signed`, `declined` or `expired`, as the contract service
 *  reports it (ACL-425 in platform). */
export type LeadContractStatus = "sent" | "dry_run" | "failed" | "viewed" | "signed" | "declined" | "expired";

/** What became of DocuSeal's e-mail invite for a press (ACL-501). */
export type LeadContractEmailStatus = "sent" | "opened" | "bounced" | "complaint";

/** `GET /leads/{id}/contracts`: one press of Send contract that reached the contract service
 *  (ACL-422, ADR-158 in platform), newest first. The link, the submission and the e-mail address are
 *  null for a viewer the contact form hides those fields from. */
export interface LeadContract {
  id: number;
  status: LeadContractStatus;
  /** The agreement the service picked, by its name; null when the press failed before it chose. */
  agreement: string | null;
  firm_tag: string | null;
  submission_id: string | null;
  /** The link the lead signs at. Whoever holds it can sign. */
  signing_url: string | null;
  /** On `failed`: the code and the sentence the rep was told. */
  error_code: string | null;
  error_detail: string | null;
  /** Whether the link left by text, and why not; null until it was tried (ACL-423). */
  texted: boolean | null;
  text_problem: string | null;
  /** The text that carried the link, as the provider last reported it: scheduled | queued | sent |
   *  delivered | failed, and on failed why. A scheduled one goes out at `text_scheduled_for`, the
   *  lead's 8 am (ACL-630). */
  text_status: string | null;
  text_failure: string | null;
  text_scheduled_for?: string | null;
  /** Whether DocuSeal e-mailed its own invite, and to whom. */
  emailed: boolean | null;
  email_to: string | null;
  /** What became of that e-mail, as the contract service read it from DocuSeal (ACL-501): the worst
   *  of sent, opened, bounced and complaint (the lead marked it as spam), and when each happened;
   *  null until reported, and absent from a core-api before ACL-501. */
  email_status?: LeadContractEmailStatus | null;
  email_sent_at?: string | null;
  email_opened_at?: string | null;
  email_bounced_at?: string | null;
  email_complaint_at?: string | null;
  /** When the lead first opened the agreement, and when they signed it (ACL-425); null until then,
   *  and absent from a core-api before ACL-425. */
  viewed_at?: string | null;
  signed_at?: string | null;
  requested_by: number | null;
  created_at: string;
  updated_at: string;
}

/** One thread: one outside number, one of our numbers, one inbox. An email thread is one lead's
 *  address and its own reply address (ADR-180 in platform). */
export interface TextConversation {
  id: string;
  /** sms | email | call */
  channel: string;
  /** For an email thread, its reply address. */
  our_number: string;
  /** For an email thread, the lead's address; "" for a viewer the lead form hides it from. */
  contact_number: string;
  /** The lead the number belongs to; null for a sender the platform does not know. */
  lead: { id: string; first_name: string | null; last_name: string | null } | null;
  /** Decided when the first text arrived, and kept. */
  inbox: "campaign" | "rep" | "unassigned";
  /** The campaign's inbox; for a rep's own number, the campaign the number sits on. */
  campaign_id: string | null;
  campaign_name: string | null;
  /** The rep whose own number it is. */
  rep: ConversationPerson | null;
  /** A rep's conversation that the campaign's inbox shows too: a text waited unread too long. */
  shared: boolean;
  /** The rep who last spoke to the lead: their "Assigned to me". */
  assignee: ConversationPerson | null;
  /** Texts nobody has opened yet. One count for everybody who shares the inbox. */
  unread: number;
  last_message_at: string;
  last_direction: "inbound" | "outbound" | null;
  /** The last text; for an email thread, the last email's summary (its label), never its body. */
  last_text: string | null;
  created_at: string;
}

/** `GET /conversations/{id}/messages`: newest first; `next_before` pages further back. */
export interface TextMessages {
  messages: TextMessage[];
  next_before: string | null;
}

/** One inbox the signed-in person reads, and what waits in it. `id` is what `GET /conversations`
 *  takes as `inbox`: assigned | mine | campaign:<id> | reps | unassigned. */
export interface TextInbox {
  id: string;
  kind: "assigned" | "mine" | "campaign" | "reps" | "unassigned";
  name: string;
  campaign_id: string | null;
  conversations: number;
  /** Conversations with a text nobody has opened. */
  unread: number;
}

/**
 * Where a callback's backup time stands (ACL-559, ADR-183 in platform): `waiting` for its first call,
 * which the dialer will make; `off`, no dialing list will make that call, so nothing will miss it;
 * `booked` as a callback of its own after nobody answered the first; `unused` once its time came
 * with nothing to book.
 */
export type CallbackBackupState = "waiting" | "off" | "booked" | "unused";

/**
 * A callback promised to the lead (ADR-080), as the record shows it: `upcoming` still ahead, `due`
 * past its time with no outcome since, `done` followed by an outcome (`closed_by`), `rescheduled`
 * replaced by a newer callback, `cancelled` moot since the lead left for an exit.
 */
export interface LeadCallback {
  id: number;
  /** The outcome that promised it. */
  disposition: string;
  due_at: string;
  /** The rep picked the time; false: the outcome's own wait from when it was set. */
  picked: boolean;
  /** `missed`: the dialer called at the promised time and nobody answered, the last redial too
   *  (ADR-156 in platform). */
  status: "upcoming" | "due" | "done" | "rescheduled" | "cancelled" | "missed";
  /** The rep it is kept for; null when nobody had the lead. */
  rep: string | null;
  /** Who set it. */
  by: string | null;
  by_user_id: number | null;
  call_id: string | null;
  campaign_id: string | null;
  created_at: string;
  closed_at: string | null;
  closed_by: string | null;
  /** What the call is for, as the rep picked it (ADR-162 in `platform`); null when none was given.
   *  Absent from an older core-api, as are the three below. */
  purpose?: string | null;
  /** The note typed with the outcome that promised it. */
  note?: string | null;
  /** The account of the rep it is kept for: a console tells its own from somebody else's. */
  rep_user_id?: number | null;
  /** How its call will be made, from the lead's list row: the dialer calls and connects the rep
   *  (`power`), the dialer offers the lead to the rep (`preview`), or nothing will and the rep
   *  calls (null). Set while it is upcoming or due. */
  dialer?: "power" | "preview" | null;
  /** The time the rep gave in case this callback's own call reaches nobody (ACL-559, ADR-183 in
   *  platform): "4 pm, or else 7 pm". Null when none was given. Absent from an older core-api, as
   *  are the two below. */
  backup_at?: string | null;
  /** Every backup time in order, `backup_at` first (ADR-210 in platform): "or else 7 pm, then 10 am
   *  tomorrow". Absent from a core-api that keeps one backup time. */
  backup_times?: string[];
  /** Where that backup time stands; null when there is none. */
  backup_state?: CallbackBackupState | null;
  /** On a callback the system booked from a missed callback's backup time: that callback. */
  backup_of?: { id: number; due_at: string } | null;
  /** Where the booking stands against its outcome's no-show limit (ADR-181); null when the
   *  outcome has none. */
  no_show?: LeadCallbackNoShow | null;
  /** The end of its window (ACL-661): its time plus the cadence's window, until when the cadence
   *  keeps trying with its own calls and redials. Null when the cadence has no window or the
   *  callback had no picked time. Absent from an older core-api. */
  until?: string | null;
}

/** A booking against its outcome's no-show limit (ADR-181). Mirrors `LeadCallbackNoShow`. */
/** `GET /leads/{id}/callback-takers`: who takes a callback outcome's call (ACL-657, ADR-216 in
 *  `platform`). */
/** A callback as the calendar shows it (ACL-658, ADR-217 in `platform`), from `GET /callbacks`. */
export interface CalendarCallback {
  id: number;
  lead_id: string;
  contact_name: string | null;
  /** The outcome that promised it, by slug. */
  disposition: string;
  due_at: string;
  picked: boolean;
  status: "upcoming" | "due" | "done" | "cancelled" | "missed";
  /** The rep it is kept for and their account; null when nobody has it yet. */
  rep: string | null;
  rep_user_id: number | null;
  by: string | null;
  by_user_id: number | null;
  /** The skills a rule asks for on the lead's stage, by name: the next free rep with them takes it. */
  only_skills: string[];
  dialer: "power" | "preview" | null;
  backup_times: string[];
  backup_state: "waiting" | "off" | "booked" | "unused" | null;
  backup_of: { id: number; due_at: string } | null;
  purpose: string | null;
  note: string | null;
  /** For the rep's screen between calls (ACL-671): the person's number and the campaign the callback
   *  names, so Call now can place the call; their accident and their role in it. Absent from an older
   *  core-api. */
  phone?: string | null;
  campaign_id?: string | null;
  accident_number?: string | null;
  role?: AccidentRole | null;
}

export interface CallbackTaker {
  slug: string;
  /** The skills a rule asks for on the stage it moves the lead to; empty with no rule. */
  only_skills: string[];
  /** The rep booking may take it. False for anyone who is not a rep. */
  booker_takes: boolean;
  /** How long it waits for its rep before another may take it; left out by an older core-api. */
  kept_minutes?: number;
}

export interface LeadCallbackNoShow {
  limit: number;
  /** The outcome set at the limit, by slug and as the catalog names it. */
  outcome: string;
  outcome_label: string | null;
  /** A missed booking: which miss it was since the lead last arrived in the outcome's stage. */
  number: number | null;
  /** An open booking: how many of the outcome's bookings were missed before it. */
  missed: number | null;
}

/** `PUT /leads/{id}/callbacks/{callback_id}/backup`: the open callback's backup times, with their
 *  offset (ACL-559). One goes as `backup_at`, which every core-api that keeps a backup takes; several
 *  as `backup_times` (ADR-210 in platform). They replace the backup times the callback had. */
export interface CallbackBackupRequest {
  backup_at?: string;
  backup_times?: string[];
}

/** One thing that happened to the lead; `kind` says which body is set. */
export interface LeadTimelineEntry {
  key: string;
  kind: TimelineKind;
  at: string;
  call_id: string | null;
  by: string | null;
  by_user_id: number | null;
  call: LeadCardCall | null;
  note: LeadNote | null;
  stage: LeadStageChange | null;
  event: LeadTimelineEvent | null;
  /** A callback, at the moment it was promised (ADR-080). */
  callback?: LeadCallback | null;
  /** A text or an email to or from the lead (ADR-095, ADR-180 in platform). */
  message?: TextMessage | null;
  /** The workflow that moved the stage or wrote the note (ACL-370), or gave the lead to somebody
   *  (ACL-408). */
  workflow?: SendingWorkflow | null;
  /** The lead given to somebody (ACL-408). */
  owner?: LeadOwnerChange | null;
  /** An ask for a closer about the lead and what its setter and closer said after it (platform
   *  ADR-189). */
  handoff?: LeadHandoffConversation | null;
}

/** `GET /leads/{id}/timeline`: newest first; `next_before` pages further back. */
export interface LeadTimeline {
  entries: LeadTimelineEntry[];
  next_before: string | null;
}

/** `POST /leads/{id}/reopen` (ADR-076): an admin took the lead out of its exit stage. */
export interface LeadReopen {
  lead_id: string;
  stage: string;
  /** The lead and the contacts with its phone that left with it. */
  reopened: string[];
  /** Dial-list rows put back on their lists. */
  lists_restored: number;
}

/** `POST /leads/{id}/disposition`: an outcome recorded off a call. */
export interface ContactOutcome {
  lead_id: string;
  disposition: string;
  stage: string | null;
  stage_moved: boolean;
  dnc: boolean;
  held: number;
  held_until: string | null;
  contract_event: boolean;
  /** When the lead is called back, for a callback outcome (ADR-080). */
  callback_at?: string | null;
  /** The backup time kept with that callback (ACL-559, ADR-183 in platform); null when none was
   *  given. Absent from an older core-api, which drops a backup time it is sent. */
  callback_backup_at?: string | null;
  /** Every backup time kept with it, in order (ADR-210 in platform). Absent from a core-api that
   *  keeps one, which saves the first alone. */
  callback_backup_times?: string[];
  /** The campaign the outcome handed the lead to, when it names one and the handover was made. */
  handed_to_campaign_id?: string | null;
  handed_to_campaign?: string | null;
}

export interface ContactBulkResult {
  action: string;
  applied: number;
  unchanged: number;
  skipped: string[];
}

/** A firm (ADR-045), as `GET /firms` lists them. */
export interface Firm {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  label: string;
  states: string[];
  /** `{"CA": 1}`: the firm's rank among each state's firms, 1 first (ADR-170). Absent from a core-api
   *  that predates it. */
  state_priorities?: Record<string, number>;
  languages: string[];
  status: string;
  pause_reason: string | null;
  settings: Record<string, unknown>;
  tags: { id: number; tag: string }[];
  contacts: number;
  /** Its book by stage group, the rows its pause holds, the signatures this month. */
  counts: {
    contacts: number;
    working: number;
    scheduled: number;
    exit: number;
    held: number;
    signed_this_month: number;
  };
  dedicated_campaign_ids: string[];
  created_at: string;
  updated_at: string;
}

// --- the live floor --------------------------------------------------------------------------------
//
// Mirrors `FloorResponse`, `FloorLine`, `AcdStatsResponse`, `LineStats` and `AgentStats` in core-api's
// `schemas.py`; a floor's reps and callers are the event-tracker's records (`WaitingRecord` in
// `services/shared/state/queues.py`), passed through as they are — hence camelCase.

/** A rep as the floor shows them: their live record, and the name on their account. */
export type FloorRep = AgentStateRecord & {
  displayName: string | null;
  /** The name of the contact on the call on hand (`GET /acd/floor` reads it off the lead); null
   *  for a contact with no name. The lead of a dial the dialer is still placing counts. */
  contactName?: string | null;
  /** The ids of the teams the person is in (ADR-195), for the floor's Team filter. Absent from a
   *  core-api that has none. */
  teams?: number[];
  /** Why the dialer gives the rep nothing, by campaign id (platform ADR-179, ACL-568): what their
   *  phone says. Absent when it says nothing. */
  noCalls?: Record<string, { reason: string; nextDueAt?: string | null }>;
};

/** A caller waiting in a line now. */
export interface WaitingCaller {
  channelId: string;
  queue: string;
  direction: string;
  sessionId?: string | null;
  /** `inbound`, `outbound` (a lead the dialer reached) or `handoff`. */
  kind?: string | null;
  joinedAt?: string | null;
  callerNumber?: string | null;
  callerName?: string | null;
  leadId?: string | null;
  /** The contact's own name, where the caller is a contact (`GET /acd/floor`). */
  contactName?: string | null;
  campaignId?: string | null;
  handoffId?: string | null;
  priority?: number | null;
  requiredSkills?: Record<string, number>;
  preferredSkills?: Record<string, number>;
  /** 1 for the next caller the line serves. */
  position?: number | null;
  /** Who the caller is for (`user@domain`): they dialed that rep's own number (ADR-037). */
  meantFor?: string | null;
  /** Nobody but that rep may take them. */
  meantForOnly?: boolean;
  /** Until when the line holds them for that rep; null once anyone may take them. */
  pinnedUntil?: string | null;
}

/** One line on the floor: its callers now, first in first. */
export interface FloorLine {
  id: number | null;
  name: string;
  campaign_id: string | null;
  campaign_name: string | null;
  waiting: number;
  longest_wait_seconds: number | null;
  callers: WaitingCaller[];
  /** The line's staffing alert, while it is on (ACL-567); absent from an older server. */
  staffing?: LineStaffingStatus | null;
}

/**
 * A line with its staffing alert on, as it stands now (ACL-567, ADR-177 in `platform`): whether it
 * is inside its staffed hours, how many reps can take its next call (Ready, ringing, on a call,
 * wrapping up or dialing on it), and the episode with nobody, if one is under way.
 */
export interface LineStaffingStatus {
  in_hours: boolean;
  can_take: number;
  /** Since when nobody, as the server's sweep last saw it; behind the live count by up to 15 s. */
  unstaffed_since: string | null;
  /** When people are told, while nobody has been yet. */
  alert_at: string | null;
  /** When the admins and supervisors were told, in the bell, and Slack first tried. */
  alerted_at: string | null;
  /** When Slack last took a message about it. */
  told_at: string | null;
  slack_channel_name: string | null;
  /** Why Slack did not take the last message: Slack's own code, or `slack_off`. */
  problem: string | null;
}

/** `GET /queues/{id}/staffing-alert`: who is told when the line has nobody to take calls. */
export interface StaffingAlert {
  queue_id: number;
  enabled: boolean;
  slack_connection_id: number | null;
  slack_connection_name: string | null;
  slack_channel_id: string | null;
  slack_channel_name: string | null;
  /** Null while the line has no alert and its campaign no timezone of its own. */
  timezone: string | null;
  hours: AvailabilityRange[];
  wait_seconds: number;
  updated_at: string | null;
  status: LineStaffingStatus | null;
}

/** `PUT /queues/{id}/staffing-alert`: the alert, written whole. */
export interface StaffingAlertInput {
  enabled: boolean;
  slack_connection_id: number | null;
  slack_channel_id: string | null;
  slack_channel_name: string | null;
  timezone: string;
  hours: AvailabilityRange[];
  wait_seconds: number;
}

/** `GET /acd/floor`: every rep and line in the supervisor's reach, live. */
export interface AcdFloor {
  reps: FloorRep[];
  lines: FloorLine[];
  handoffs_pending: number;
}

/** One line's callers over a window, as `src/acd_stats.py` counts them. Rates are 0–1, and null
 *  where there was nothing to divide. */
export interface LineStats {
  queue: string;
  offered: number;
  connected: number;
  dropped: number;
  abandoned: number;
  timed_out: number;
  moved: number;
  removed: number;
  in_progress: number;
  handoffs: number;
  relaxed: number;
  ronas: number;
  answer_rate: number | null;
  abandon_rate: number | null;
  service_level: number | null;
  wait_avg: number | null;
  wait_p50: number | null;
  wait_p90: number | null;
  wait_max: number | null;
  abandon_wait_avg: number | null;
  ring_avg: number | null;
  ring_p90: number | null;
  ring_max: number | null;
  talk_avg: number | null;
  talk_total: number;
  offers_avg: number | null;
}

/** One rep's calls over a window, and their time in each effective state. */
export interface AgentStats {
  user: string;
  domain: string;
  calls: number;
  talk_total: number;
  talk_avg: number | null;
  ring_avg: number | null;
  ronas: number;
  dispositioned: number;
  /** Effective state → seconds, clipped to the window. */
  time_in_state: Record<string, number>;
}

/** `GET /acd/stats`: the callers who joined in the window. */
export interface AcdStats {
  since: string;
  until: string;
  sla_seconds: number;
  totals: LineStats;
  lines: LineStats[];
  agents: AgentStats[];
}

/** What the dialer's actions answer: it was told, as a 202, not that it has done it. */
export interface DialerActionResponse {
  command: string;
  campaign_lead_id: number | null;
  call_id: string | null;
}

/**
 * `GET /dialer/calls/{id}/detection`: what answering machine detection made of a dialer call the
 * rep is on (ADR-104, ADR-106 in `platform`), and what the cadence says this attempt does on a
 * voicemail (ADR-107). The phone shows its suggestion on `suggest_voicemail` and makes the
 * cadence's choice the main one; the rest says why.
 */
export interface CallDetection {
  call_id: string;
  /** `off` when nothing listened to the call. */
  mode: string;
  passive: boolean;
  /** human | machine | notsure; null while the switch listens, and for good when nothing did. */
  result: string | null;
  cause: string | null;
  decided_at: string | null;
  /** A passive detection heard a machine on a call that is still up. */
  suggest_voicemail: boolean;
  /** What the cadence says this attempt does when a voicemail answers it: hang up, or leave a
   *  message, a rep's (`leave_message`) or the dialer's own (`leave_message_auto`, which is a
   *  rep's wherever a rep is on the call). Null when no cadence call is behind the dial, and
   *  absent from a core-api that does not say. */
  on_voicemail?: "hang_up" | "leave_message" | "leave_message_auto" | null;
  /** The message the cadence names for this attempt, by its slug; null is the lead's own. */
  voicemail_message?: string | null;
  /** Which attempt of its call this is (0 the call, k its k-th redial), and how many redials the
   *  call carries. Null for a call with none. */
  redial?: { n: number; of: number } | null;
  /** The redial that follows at once when the rep hangs up on this voicemail, or null. */
  next_redial?: number | null;
  /** The message core-api prepared for this call (ADR-128): the one Leave voicemail plays. Null
   *  until one is picked, and for good on a tenant with no voice set up. Absent from a core-api
   *  that predates it. */
  greeting?: CallGreeting | null;
  /** The greeting is being picked or made right now: the phone asks again. */
  greeting_expected?: boolean;
}

/**
 * The message a call leaves on a voicemail, as core-api prepared it (ADR-128 in `platform`): a
 * recording of the library, or a greeting written as text, filled for this lead and spoken in the
 * name and the voice of the rep the call was dialed for.
 */
export interface CallGreeting {
  /** What a drop names. */
  slug: string;
  name: string;
  kind: "recording" | "script" | string;
  status: "making" | "ready" | "failed" | string;
  /** A script's words as this reader may see them: a field hidden from them reads "(hidden)".
   *  Null for a recording. */
  words: string | null;
  /** Which of a script's two texts this is (ADR-116): `b` because nobody has reached the lead for a
   *  day. Null for a recording. */
  version?: "a" | "b" | null;
  /** The voice that speaks it, by the name ElevenLabs gives it. For the rep's own voice
   *  (`voice_own`, ADR-117), the rep's name. */
  voice: string | null;
  voice_own?: boolean;
  /** Why it could not be made, in a sentence. */
  problem: string | null;
}

/** The message a call left on a voicemail (ADR-128 in `platform`), on the lead's record. */
export interface CallVoicemail {
  slug: string;
  name: string;
  /** What was said, for a greeting written as text, as this reader may see it. */
  words: string | null;
  /** Which of a script's two texts was left (ADR-116): `a` or `b`. Null for a recording. */
  version?: "a" | "b" | null;
  /** The voice that spoke it. For the rep's own voice (`voice_own`), the rep's name. */
  voice: string | null;
  voice_own?: boolean;
  /** played_full | failed, or null while nothing has said how it went. */
  result: string | null;
}

/** A person's own voice (ADR-117 in `platform`): that they have one, and since when. Never the
 *  voice's id at ElevenLabs, and never the recording, which is kept nowhere. */
export interface OwnVoice {
  created_at: string;
  /** How long the recording was, in seconds. */
  seconds: number | null;
  /** When they agreed to their voice being copied and used for their greetings. */
  agreed_at: string;
}

/** `GET /auth/me/voice`: the voice that speaks for me, and what a recording has to be. */
export interface MyVoice {
  gender: "female" | "male" | null;
  own_voice: OwnVoice | null;
  /** Whether a voice can be made now: the tenant has an ElevenLabs account connected. */
  can_record: boolean;
  /** What I agree to, word for word. A recording is taken only with my yes to this. */
  agreement: string;
  min_seconds: number;
  max_seconds: number;
}

/** A message for a lead's voicemail: a recording, or a script that is filled for each lead. */
export interface VoicemailMessage {
  id: number;
  tenant_id: number;
  /** What a drop names. Fixed at create: core-api has no way to rename one. */
  slug: string;
  name: string;
  /** What it is (ADR-112 in `platform`): a file the switch plays, or text with fields. A core-api
   *  from before scripts sends none, and every message it has is a recording. */
  kind?: "recording" | "script";
  /** `en` or `es`. A script always has one; a recording may have none. */
  language: string | null;
  /** Who it is for: the lead stages it lists, or none for the tenant's general message. A core-api
   *  from before a message could list several sends only `stage`. */
  stages?: string[];
  /** The first of `stages`, or null: the one stage a message had before it could list several. */
  stage: string | null;
  /** A recording's: what the switch plays, a file from the audio store by its URL, or — from before
   *  the store — a sound file on the node or a stream it makes itself. Null on a script. */
  media: string | null;
  /** A script's: the greeting's text with its fields. Null on a recording. */
  script?: string | null;
  /** A script's version B (ADR-116 in `platform`): what a lead hears instead once nobody has
   *  reached them for a day. Null when the greeting has one version. */
  script_b?: string | null;
  /** The fields a script uses, in either version, that a greeting can no longer use: a custom field
   *  that was unticked or archived since (ADR-113). Each reads as having no value. */
  unusable_fields?: string[];
  /** False once retired: off reps' phones, and a drop that names it is refused. */
  active: boolean;
  created_at: string;
}

/** A field a script can use, as `GET /voicemail-messages/fields` lists it for the Insert field menu. */
export interface GreetingField {
  /** How a script writes it: `{{lead.first_name}}`. */
  name: string;
  label: string;
  /** The menu's heading: Lead, Rep, Firm, Campaign, Callback. */
  group: string;
  /** How its value is spoken: name, text, number, phone, date or datetime. */
  type: string;
  /** Where the value comes from, in a line. */
  hint: string;
  /** A value as it would be spoken. */
  example: string;
}

/** A greeting as one lead would hear it (`POST /voicemail-messages/preview`). */
export interface GreetingPreview {
  /** The lead as the pick saw them: their stage, and their language (English when they have none). */
  stage: string | null;
  language: string;
  /** The library's message for the lead. Null for a draft, and when nothing fits them. */
  message: VoicemailMessage | null;
  /** The words: the draft's, or the picked script's. Null when the pick is a recording, or nothing. */
  text: string | null;
  /** Which of the picked script's two texts the words are (ADR-116): `a`, or `b` because nobody has
   *  reached the lead for a day. Null for a draft, a recording, or nothing. */
  version?: "a" | "b" | null;
  /** The first call that reached nobody since the lead entered their stage or a rep last spoke to
   *  them, and from when version B is what they hear. Null when every call reached them. */
  unanswered_since?: string | null;
  b_from?: string | null;
  /** A draft's fields with no value for this lead and no fallback words. */
  missing: string[];
  /** What keeps a draft from being saved, as sentences. */
  problems: string[];
  /** Scripts for the lead's stage and language that were passed over, and the fields they lacked. */
  skipped: { slug: string; name: string; missing: string[] }[];
}

// --- skills (ADR-015) ----------------------------------------------------------------------------
//
// Mirrors `RoutingSkillResponse`, `UserSkill` and `UserSkillsResponse` in core-api's `schemas.py`.

/**
 * A skill in the tenant's catalog: `setter`, `closer`, `spanish`. A line or a number requires or
 * prefers skills, a handoff asks for `closer`, and the ACD rings only a rep who holds each one it
 * requires at the level asked.
 */
export interface RoutingSkill {
  id: number;
  tenant_id: number;
  /** Immutable: lines, numbers, handoffs and reps all name a skill by it. */
  slug: string;
  name: string;
  /** `role`, `language`, or whatever the tenant groups by. */
  category: string | null;
  description: string | null;
  /** Reps can hold it, and a line, a number or a handoff can require it. */
  is_agent_skill: boolean;
  /** It describes a lead: the dialer turns a lead's tag into a preference or a requirement. */
  is_lead_tag: boolean;
  /** False once retired. Reps keep their level for the day it comes back; the ACD ignores it. */
  active: boolean;
  /** Reps holding it, at any level. */
  holders: number;
}

/** One skill a rep holds, at a level from 1 to 5. */
export interface UserSkill {
  slug: string;
  level: number;
}

/** Where an invitation stands (ADR-028 in `platform`): waiting, past its 72 hours, or used. */
export type InvitationStatus = "pending" | "expired" | "accepted";

/** `GET /invitations`: somebody an admin has asked to join, and who they will be once they do. */
export interface Invitation {
  id: number;
  email: string;
  name?: string | null;
  display_name: string | null;
  role: Role;
  tenant_id: number | null;
  tenant_name: string | null;
  domain_id: number | null;
  domain_name: string | null;
  /** Where an admin's or superadmin's phone goes. Null for the roles locked to a domain. */
  endpoint_domain_id: number | null;
  endpoint_domain_name: string | null;
  /** Each is staffed on its primary line at acceptance. `name` is null once it has been deleted. */
  campaigns: { id: string; name: string | null }[];
  skills: UserSkill[];
  /** The number held for them, E.164, which rings them on their first campaign once they accept. */
  number?: string | null;
  /** Whether accepting gave them that number: null until they accept, false when it had been
   *  routed elsewhere meanwhile. */
  number_given?: boolean | null;
  status: InvitationStatus;
  expires_at: string;
  /** How many times it has been sent, the first time included. */
  sent_count: number;
  last_sent_at: string;
  /** When the SMTP server took the last send's email. Null with `email_error` null: none was tried. */
  emailed_at: string | null;
  email_error: string | null;
  invited_by_id: number | null;
  invited_by_name: string | null;
  accepted_at: string | null;
  accepted_user_id: number | null;
  created_at: string;
}

/** The answer to sending or sending again: the only time an invitation's link comes back. */
export interface InvitationSent extends Invitation {
  invite_url: string;
  /** Whether this server emails invitations at all. False: the admin sends the link themselves. */
  email_enabled: boolean;
}

/** One person of `POST /invitations/bulk`: their invitation, or why none was made. */
export interface InvitationBulkResult {
  email: string;
  /** The invitation made for this address, with its link. Null when none was. */
  invitation: InvitationSent | null;
  /** Why none was: the address already has an account, or an invitation not yet accepted. */
  error: string | null;
}

/** The answer to inviting several people: a result for each, in the order they were given. */
export interface InvitationBulkSent {
  results: InvitationBulkResult[];
  /** Whether this server emails invitations at all. False: the admin sends the links themselves. */
  email_enabled: boolean;
}

/** `POST /auth/invitation`: what the person is joining, for the page their link opens. */
export interface InvitationPreview {
  email: string;
  /** Their real name, as the admin gave it (ADR-172 in `platform`). */
  name?: string | null;
  display_name: string | null;
  role: Role;
  tenant_name: string | null;
  invited_by_name: string | null;
  /** The campaigns they will staff, by name. */
  campaigns: string[];
  skills: { slug: string; name: string; level: number }[];
  /** Their own number, E.164, when the invitation holds one. */
  number?: string | null;
  status: InvitationStatus;
  expires_at: string;
}

/** `POST /app-users/{id}/sign-in-link` (ADR-040 in `platform`): the one time a sign-in link is
 *  shown. It signs a browser in as the person, once, until `expires_at`. */
export interface SignInLinkMade {
  url: string;
  expires_at: string;
  user_id: number;
  email: string;
  display_name: string | null;
}

/** `GET /app-users/{id}/skills`: a rep's skills, on the phone the ACD rings. */
export interface UserSkills {
  user_id: number;
  /** The SIP identity the ACD knows the rep by; null for an account with no phone. */
  user: string | null;
  domain: string | null;
  skills: UserSkill[];
}

/** One setting that plays a store file (`AudioUse` in core-api). */
export interface AudioUse {
  /** `line`, `campaign` or `voicemail_message`. */
  kind: string;
  id: string;
  name: string;
  /** `moh`, `entry_announcement`, `outbound_hold_media`, `safe_harbor_media` or `media`. */
  setting: string;
  /** The campaign to open to change it; null for a voicemail message or a standalone line. */
  campaign_id: string | null;
}

/** A file in the tenant's audio store (ADR-020 in platform). */
export interface AudioFile {
  id: number;
  /** What the file's URL names it by. */
  uuid: string;
  tenant_id: number;
  name: string;
  description: string | null;
  tags: string[];
  mime: string;
  size_bytes: number;
  /** Read off the WAV header at upload; null when the header did not say. */
  duration_ms: number | null;
  sample_rate: number | null;
  channels: number | null;
  created_at: string;
  updated_at: string;
  /** What a setting stores and the switch fetches — and what the console plays to preview it. */
  url: string;
  /** Every setting that plays it. A file in use cannot be deleted. */
  used_by: AudioUse[];
}

// --- numbers, routing and campaigns ------------------------------------------------------------
//
// Mirrors `services/core-api/src/schemas.py` (QueueResponse, CampaignResponse,
// PhoneNumberResponse, NumberOrderResponse, DidResponse) and `src/numbers.py` (ROUTE_TYPES).

/** One skill a line asks of a rep, at a minimum level (ADR-015). */
export interface SkillRequirement {
  slug: string;
  min_level: number;
}

/**
 * An ACD line (a `queues` row): where a call waits for a rep, and the settings it waits under.
 * A campaign owns its lines (ADR-014) and creates its primary one with itself; a standalone line
 * is a routing target a number names directly.
 */
export interface Queue {
  id: number;
  /** The `acl_queue` value. Fixed once calls and reps use it, even if the campaign is renamed. */
  name: string;
  description: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  /** `primary`, `closer`, `inbound` or `custom`; null for a standalone line. */
  purpose: string | null;
  enabled: boolean;
  ring_timeout_seconds: number;
  /** one_at_a_time | ring_all — how many reps one offer rings (ADR-049). */
  ring_strategy: string;
  /** A caller who calls back rings the rep who last spoke to them first, when that rep is free
   *  (ADR-072). */
  previous_rep_first: boolean;
  wrap_up_seconds: number;
  disposition_seconds: number;
  /** 0 = a caller waits as long as it takes. */
  max_wait_seconds: number;
  moh: string | null;
  outbound_hold_media: string | null;
  entry_announcement: string | null;
  required_skills: SkillRequirement[];
  preferred_skills: SkillRequirement[];
  skill_strategy: string;
  skill_relax_seconds: number;
  default_priority: number;
  /** Who takes an inbound caller we know, by their contact's stage (ADR-073). */
  stage_rules: StageRule[];
  member_count: number;
}

/** One of the tenant's stages (ADR-077). Mirrors core-api's `StageResponse`. */
export interface StageRow {
  slug: string;
  label: string;
  description: string | null;
  group: "working" | "scheduled" | "exit";
  /** An exit's reach: every record with the phone, or only the same firm's. */
  exit_scope: "all_firms" | "same_firm" | null;
  /** The dialer ranks a lead in it near the top, as About to sign always was. */
  dial_boost: boolean;
  sort_order: number;
  /** One of the platform's four: renamed and reordered, never deleted. */
  system: boolean;
  /** The board column's tint (`STAGE_COLORS`); null for the pick by group. Absent from older APIs. */
  color?: StageColor | null;
  /** The tenant's leads in it now. */
  leads: number;
}

/** `services.shared.stages.COLORS`: names, so each theme keeps its own shade. */
export const STAGE_COLORS = [
  "blue", "indigo", "purple", "pink", "red", "orange", "peach", "yellow", "green", "teal", "gray",
] as const;
export type StageColor = (typeof STAGE_COLORS)[number];

/** What deleting a stage moved to its replacement (`DELETE /stages/{slug}`). */
export interface StageRemoved {
  slug: string;
  replaced_by: string | null;
  leads: number;
  dispositions: number;
  line_rules: number;
  voicemail_messages: number;
  /** The campaigns' cadences for it (platform ADR-087): each now runs the replacement, unless the
   *  campaign already had one for that stage (ACL-543). Absent from an older core-api. */
  stage_cadences?: number;
}

// --- forms: the sections and fields a record is drawn from (ACL-255, ADR-087) -----------------------

/** What a form describes: the contact (`lead`) or a firm. Mirrors core-api's `FormEntity`. */
export type FormEntity = "lead" | "firm";

/** The eleven types a field can have. Mirrors core-api's `FieldType`; `lib/record-fields.ts` has
 *  how each is shown and typed in. */
export type FormFieldType =
  | "text"
  | "long_text"
  | "number"
  | "email"
  | "phone"
  | "date"
  | "datetime"
  | "boolean"
  | "dropdown"
  | "multi_select"
  | "reference";

/** Where a field's value lives: `value` for an admin's own field; `column` and `attribute` for the
 *  platform's, which are written where they always were (ACL-259 puts them on the form). */
/** Where a field's value lives: a row of the record's own (`value`), the record's column or attribute
 *  (the platform's own fields), or the record's accident, one value for everyone in it (`accident`,
 *  ADR-221 in the platform repo: the facts of the crash). */
export type FormFieldStorage = "value" | "column" | "attribute" | "accident";

/** One choice of a dropdown or a multi-select. The key is what a record stores and never changes;
 *  the label can. */
export interface FormFieldOption {
  key: string;
  label: string;
}

/** A field's settings, by type: a text's length, a number's range, a choice's options, a
 *  reference's target. The other types have none. */
export interface FormFieldConfig {
  /** `text` and `long_text`. */
  max_length?: number;
  /** `number`. */
  min?: number;
  max?: number;
  decimals?: number;
  /** `dropdown` and `multi_select`. */
  options?: FormFieldOption[];
  /** `reference`: the kind of record it points at, and which of that form's fields names it. */
  target?: FormEntity;
  display?: string;
}

/** What the signed-in person may do with a field of a record. A field hidden from their role is
 *  not sent at all, so there is no third value. */
export type FieldAccess = "read" | "edit";

/** What a reference field's stored id points at, as the reader may see it. `ok`: the record and its
 *  label; `missing`: it no longer exists; `hidden`: it exists but this reader may not open it, so it is
 *  not named. Mirrors core-api's `RecordReference`. */
export interface RecordReference {
  id: number | string;
  label: string | null;
  /** `archived`: a firm set to done (ACL-261): still named and linked, never offered for a new link. */
  state: "ok" | "archived" | "missing" | "hidden";
}

/** One record a reference picker offers (`GET /forms/{entity}/fields/{key}/references`). */
export interface ReferenceOption {
  id: number | string;
  label: string;
  /** A second, quieter line: a firm's code, a contact's phone. */
  detail: string | null;
}

export interface ReferenceSearch {
  items: ReferenceOption[];
  /** Where the next page starts, or null when there is none. */
  next_offset: number | null;
}

/** What the caller's own search of one kind of record looks in besides the platform's own fields
 *  (`GET /forms/{entity}/searchable`, ADR-101): the fields an admin marked searchable that this person
 *  may see, in the form's order. A superadmin over several tenants gets every tenant's, so a title may
 *  come more than once. Mirrors core-api's `SearchedFieldsResponse`. */
export interface SearchedFields {
  entity: FormEntity;
  fields: { key: string; title: string; type: FormFieldType }[];
}

/** A field of a record with its value (`GET /leads/{id}/fields`). Mirrors core-api's
 *  `RecordFieldResponse`. `value` is what is stored, by type: a string for text, email, phone
 *  (E.164), date (`YYYY-MM-DD`), date and time (ISO in UTC) and dropdown (the option's key); a
 *  number; a boolean; a list of option keys for a multi-select; a record's id for a reference;
 *  null when nothing is stored. */
export interface RecordField {
  key: string;
  title: string;
  type: FormFieldType;
  required: boolean;
  sort_order: number;
  system: boolean;
  storage: FormFieldStorage;
  config: FormFieldConfig;
  value: unknown;
  access: FieldAccess;
  can_edit: boolean;
  /** For a reference that holds a value: what it points at. */
  reference?: RecordReference | null;
}

export interface RecordSection {
  key: string;
  title: string;
  sort_order: number;
  fields: RecordField[];
}

/** A contact's sections and fields with their values, in the form's order. */
export interface LeadFields {
  lead_id: string;
  sections: RecordSection[];
}

/** A firm's sections and fields with their values (`GET /firms/{id}/fields`). */
export interface FirmFields {
  firm_id: number;
  sections: RecordSection[];
}

/** The roles an admin sets access for. Admins and superadmins always edit, so no rule names them. */
export type AccessRole = "supervisor" | "rep";
/** What a rule gives a role on a folder or a field: not shown at all, shown, or shown and editable. */
export type AccessLevel = "hidden" | "read" | "edit";

/** One role's access to a folder or a field. A field's rule wins over its folder's; a role with
 *  neither reads. Mirrors core-api's `FormAccessRule`. */
export interface FormAccessRule {
  role: AccessRole;
  level: AccessLevel;
}

/** A field as the admin's design reads it (`GET /forms/{entity}/design`, admin only): the definition,
 *  its own access rules and how many records hold a value. */
export interface AdminFormField {
  key: string;
  title: string;
  type: FormFieldType;
  required: boolean;
  sort_order: number;
  /** One of the platform's own fields: renamed, moved and given rules, never retyped or archived. */
  system: boolean;
  storage: FormFieldStorage;
  config: FormFieldConfig;
  archived: boolean;
  /** The field's own rules; a role absent here follows the folder. */
  rules: FormAccessRule[];
  /** The contacts search and the pickers also look in it (ADR-101): at most six per form. */
  searchable: boolean;
  /** A voicemail greeting may say its value to the lead (ADR-113 in `platform`): an admin's tick. A
   *  core-api from before it sends none, and no field is ticked. */
  speakable?: boolean;
  /** How many records hold a value for it. */
  values: number;
  created_at?: string | null;
}

export interface AdminFormSection {
  key: string;
  title: string;
  sort_order: number;
  archived: boolean;
  /** The folder's rules; a role absent here reads. */
  rules: FormAccessRule[];
  /** Every field of the folder, archived ones too, in order. */
  fields: AdminFormField[];
  created_at?: string | null;
}

/** The whole design of one form: every folder in order, archived ones too. */
export interface AdminForm {
  entity: FormEntity;
  title: string;
  sections: AdminFormSection[];
}

/** A line's rule for callers in one stage (ADR-073). Mirrors core-api's `StageRule`: `only` reps
 *  with `skills` may take the caller, or those reps ring `first` and then anyone. For that caller it
 *  replaces the line's skills and the number's; their language still counts. */
export interface StageRule {
  stage: string;
  mode: "only" | "first";
  skills: SkillRequirement[];
  /** `only`: after this many seconds anyone on the line may take the caller too. Null: they wait
   *  for the rule's reps, up to the line's own wait limit. */
  fallback_seconds: number | null;
  /** The rule also says who the dialer gives the stage's leads to (ACL-656, ADR-216 in `platform`).
   *  Absent from an older core-api. */
  dialer?: boolean;
}

/**
 * A law firm's intake programme: firm + state + language, the lines its calls are served from,
 * and how the dialer works its lists (ADR-019). Mirrors `CampaignResponse`.
 */
export type RepNumberFallback = "line" | "rep_only";
/** Where a text to a rep's own number goes (ADR-095): to the rep and, once it has waited unread, the
 *  campaign's inbox too; or to the rep alone. */
export type RepTextFallback = "campaign" | "rep_only";

export interface Campaign {
  id: string;
  tenant_id: number;
  tenant_name: string | null;
  name: string;
  /** The CRM's firm tag (`lhpark_ca`) — what intake resolves to this row. */
  slug: string;
  state: string | null;
  language: string;
  /** The primary line. core-api sets it when it creates the campaign; no client moves it. */
  queue_id: number | null;
  queue_name: string | null;
  /** Inbound routing: a disabled campaign's numbers announce and hang up. */
  enabled: boolean;
  /** `draft`, `active`, `paused` or `done`. Only an active campaign is dialed. */
  status: string;
  /** inbound | outbound | both (ADR-060). */
  purpose: string;
  dial_mode: string;
  language_routing: string;
  force_auto_answer: boolean;
  /** Which of a rep's dialing campaigns gives them their next lead; higher first (ADR-054). */
  dial_priority: number;
  dial_timeout_seconds: number;
  preview_timeout_seconds: number;
  /** HH:MM on the lead's own clock. */
  calling_window_start: string;
  calling_window_end: string;
  respect_lead_timezone: boolean;
  default_timezone: string | null;
  abandon_after_seconds: number;
  safe_harbor_action: string;
  safe_harbor_media: string | null;
  /** Answering machine detection on the dialer's calls (ADR-104): off | basic. */
  amd_mode: string;
  /** Whether that detection only suggests (ADR-106): the rep is rung at the answer, and a
   *  machine is shown to them instead of hung up for them. */
  amd_passive: boolean;
  /** In order; empty = each rep's domain routing. */
  outbound_gateway_ids: number[];
  /** What every dialer call presents; null = the rep's own number, then the domain's default. */
  caller_id_number: string | null;
  /** 0 = a lead never ages off a list. */
  list_horizon_days: number;
  /** What happens when a caller dials a rep's own number here and that rep cannot take the call
   *  (ADR-037): `line`, anyone on the line after the wait; `rep_only`, nobody else, and the
   *  closing message ends the call. */
  rep_number_fallback: RepNumberFallback;
  rep_number_wait_seconds: number;
  rep_number_message: string | null;
  /** A rep with their own number here presents it on their dialer calls for the campaign. */
  rep_number_caller_id: boolean;
  /** Its reps may choose My calls only. */
  own_calls_only_allowed: boolean;
  /** A text to a rep's own number here (ADR-095): `campaign`, the rep and then the campaign's inbox
   *  once it has waited `rep_text_wait_seconds` unread; `rep_only`, the rep alone. Absent from a
   *  core-api that does not keep texts yet. */
  rep_text_fallback?: RepTextFallback;
  rep_text_wait_seconds?: number;
  /** The cadence the campaign runs (ADR-066, ADR-067): its only dialing policy, so the attempts a
   *  lead gets, the waits between them and the daily cap on a number are its. Null only while the
   *  dialer is off; a dial mode with no cadence is refused. */
  cadence_id: number | null;
  cadence: CadenceRef | null;
  /** What an admin should know about the cadence as it stands; said, not refused. */
  cadence_warnings: string[];
  /** The stages that run a cadence of their own (ADR-087); a stage not listed runs `cadence`.
   *  Absent from a core-api that predates it. */
  stage_cadences?: CampaignStageCadence[];
  /** The primary line, with its settings. */
  routing: Queue | null;
  /** Every line the campaign owns, the primary among them. */
  lines: Queue[];
  did_count: number;
  created_at: string;
  updated_at: string;
}

/** Who staffs a line: the phone the ACD rings and the person holding it. Mirrors
 *  `QueueMemberResponse`. */
export interface QueueMember {
  queue_id: number;
  queue_name: string;
  user: string;
  domain: string;
  enabled: boolean;
  sip_endpoint_id: number | null;
  app_user_id: number | null;
  display_name: string | null;
  /** The skills the ACD sees on this rep: slug → level. */
  skills: Record<string, number>;
  /** Somebody put them on this line, rather than a team (platform ADR-185). Absent from a
   *  core-api older than teams, where every rep was added by hand. */
  by_hand?: boolean;
  /** The teams that put them on this line, by name. */
  teams?: string[];
}

/** One person in a team (platform ADR-185), with their own skills: a team gives nobody a skill. */
export interface TeamPerson {
  user_id: number;
  display_name: string | null;
  email: string;
  role: Role;
  is_active: boolean;
  sip_endpoint_id: number | null;
  /** The extension the ACD rings; null for somebody with no phone, who is on no line. */
  extension: string | null;
  skills: Record<string, number>;
  other_teams: string[];
}

/** A campaign line a team staffs, with what it asks of a rep. */
export interface TeamLine {
  queue_id: number;
  queue_name: string;
  purpose: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  required_skills: SkillRequirement[];
}

/** Somebody in a chat, or somebody to message (platform ADR-186). */
export interface ChatPerson {
  user_id: number;
  name: string | null;
  role: Role;
  is_active: boolean;
  extension: string | null;
  /** Their profile picture's address (platform ADR-079), which someone in a chat with them may
   *  read. Absent from a core-api that has none. */
  avatar_url?: string | null;
}

/** `GET /chats`: a chat the person is in, a team's, a direct one or a contact's, newest first. */
export interface ChatSummary {
  id: number;
  /** A contact's is where the setter and the closer of its handoffs talk (platform ADR-189). */
  kind: "team" | "direct" | "contact";
  /** The team's name, the other person's in a direct chat, or the contact's in theirs. */
  title: string;
  team_id: number | null;
  /** The team was deleted: the chat is kept to read, and takes no more messages. */
  archived: boolean;
  /** The other person in a direct chat. */
  other: ChatPerson | null;
  /** The contact a contact's conversation is about (platform ADR-189). Absent from a core-api
   *  that has none. */
  contact?: SharedContact | null;
  member_count: number;
  unread: number;
  last_message: {
    id: number;
    sender_id: number | null;
    sender_name: string | null;
    preview: string;
    has_contact: boolean;
    created_at: string;
    /** The ask for a closer this message is the card of (platform ADR-187). */
    handoff?: CloserAsk | null;
    /** It mentions the reader (platform ADR-186, "Mentions"). Absent from a core-api that has none. */
    mentions_me?: boolean;
  } | null;
  /** The reader is a guest here: in an open team's chat without being in the team (platform
   *  ADR-190). No toast pops for it, and they may leave it. Absent from a core-api that has none. */
  guest?: boolean;
  /** An admin's view of a team's chat they are not in (platform ADR-207): listed under "Other
   *  teams", read and written there, never unread and never a toast. Absent from a core-api that has
   *  none. */
  other_team?: boolean;
  /** Unread replies in the threads the reader follows here (platform ADR-191); in `unread` too. */
  thread_unread?: number;
  /** The newest reply in a thread the reader follows here, for its toast. */
  last_reply?: {
    id: number;
    thread_id: number;
    sender_id: number | null;
    sender_name: string | null;
    preview: string;
    created_at: string;
    mentions_me?: boolean;
  } | null;
}

export interface ChatDetail extends ChatSummary {
  members: (ChatPerson & { last_read_id: number | null; guest?: boolean })[];
}

/** `GET /chats/people/{id}/status`: where somebody one shares a chat with is on the phone now
 *  (platform ADR-186, "Status"). Never the call itself. */
export interface ChatPersonStatus {
  /** The ACD's state (available, busy, paused, ...); `signed_out` when they take no line's calls,
   *  `no_phone` when the account has no phone. */
  state: string;
  since: string | null;
  /** Why they paused, while paused. */
  code: string | null;
  /** Their phone is connected, as far as core-api can tell. */
  phone: boolean | null;
}

/** `GET /chats/teams`: a team whose chat anyone of the tenant may join (platform ADR-190). */
export interface OpenTeamChat {
  team_id: number;
  name: string;
  /** The team's people and its leads; guests are not counted. */
  people: number;
  /** The reader is in its chat already, as one of its people or a guest. */
  joined: boolean;
  chat_id: number | null;
}

/** A contact shared in a message. A reader who may not open them is told only that. */
export interface SharedContact {
  lead_id: string;
  may_open: boolean;
  name: string | null;
  stage: string | null;
  phone_last4: string | null;
}

export interface ChatMessage {
  id: number;
  chat_id: number;
  sender_id: number | null;
  sender_name: string | null;
  sender_role: Role | null;
  /** The sender's profile picture's address (platform ADR-079). */
  sender_avatar_url?: string | null;
  body: string;
  contact: SharedContact | null;
  client_id: string | null;
  created_at: string;
  /** This message is the card of an ask for a closer (platform ADR-187). */
  handoff?: CloserAsk | null;
  /** The people it mentions ("@Maria Lopez" in its words; platform ADR-186, "Mentions"). Absent
   *  from a core-api that has none. */
  mentions?: { user_id: number; name: string | null }[];
  /** A reply: the thread's first message (platform ADR-191). */
  thread_id?: number | null;
  /** The thread this message starts, once it has replies. */
  thread?: ChatThreadSummary | null;
  /** The emoji people put on it (platform ADR-186, "Reactions"). Absent from a core-api that has
   *  none. */
  reactions?: ChatReaction[];
}

/** One emoji on a message (platform ADR-186, "Reactions"), in the order it was first put there. */
export interface ChatReaction {
  emoji: string;
  count: number;
  /** The reader is one of them. */
  mine: boolean;
  /** Who, the first to react first; ten at most, `count` says how many in all. */
  names: string[];
}

/** A message's thread (platform ADR-191), as the line under the message says it. */
export interface ChatThreadSummary {
  reply_count: number;
  last_reply_at: string | null;
  /** Who replied, the latest first, three at most. */
  repliers: { user_id: number; name: string | null; avatar_url?: string | null }[];
  /** Replies from others the reader has not read, when they follow it. */
  unread: number;
  following: boolean;
}

/** `GET /chats/{id}/threads/{root}`: a message and its replies, oldest first. */
export interface ChatThread {
  root: ChatMessage;
  replies: ChatMessage[];
  following: boolean;
}

/** Where an ask for a closer is: open while `waiting` or `taken`, else how it ended. */
export type CloserAskState = "waiting" | "taken" | "transferred" | "cancelled" | "expired" | "ended";

/** An ask for a closer (platform ADR-187), as the person reading it sees it. */
export interface CloserAsk {
  id: number;
  state: CloserAskState;
  chat_id: number;
  team_id: number | null;
  team_name: string | null;
  asked_user_id: number | null;
  asked_user_name: string | null;
  asked_by: number | null;
  asked_by_name: string | null;
  caller_channel_id: string;
  lead_id: string | null;
  campaign_id: string | null;
  queue_name: string | null;
  note: string | null;
  taken_by: number | null;
  taken_by_name: string | null;
  /** The closer's phone, for the setter's transfer naming them. */
  taken_by_sip_endpoint_id: number | null;
  taken_at: string | null;
  hold_until: string | null;
  wait_until: string;
  handoff_id: string | null;
  closed_at: string | null;
  created_at: string;
  /** The reader asked it. */
  mine: boolean;
  /** The reader took it. */
  took: boolean;
  /** The reader may press Take call now; else `cannot_take` says why. */
  can_take: boolean;
  cannot_take: string | null;
}

/** One line of a drafted handoff note (platform ADR-188) and where it came from: the call's
 *  transcript, the contact's fields, both, or neither (not asked yet). */
export interface CloserNoteLine {
  label: string;
  value: string;
  source: "call" | "contact" | "both" | "none";
}

/** `POST /handoff-requests/draft`: the note for a closer, drafted from the call so far. */
export interface CloserNoteDraft {
  note: string;
  lines: CloserNoteLine[];
  /** How many transcript lines it was written from; 0 when the call was not used. */
  transcript_lines: number;
  model: string | null;
  /** Why the call was not used, when it was not. */
  notice: string | null;
}

/** `GET /handoff-requests/targets`: who a setter on a call can ask for a closer. */
export interface CloserAskTargets {
  queue_id: number;
  queue_name: string;
  teams: { team_id: number; name: string; closers: number; ready: number }[];
  people: {
    user_id: number;
    name: string | null;
    extension: string | null;
    state: string;
    holds_closer: boolean;
  }[];
  open: CloserAsk | null;
}

/** A team's lead (platform ADR-207): a supervisor or an admin. In the team's chat, and not one of
 *  its `members` unless they are in the team too. */
export interface TeamLead {
  user_id: number;
  display_name: string | null;
  email: string;
  role: string;
}

/** `GET /teams`: a tenant's group of people with its leads, and the lines it staffs (ADR-185,
 *  ADR-207). */
export interface Team {
  id: number;
  tenant_id: number;
  name: string;
  lead_user_ids: number[];
  leads: TeamLead[];
  members: TeamPerson[];
  lines: TeamLine[];
  /** Anyone of the tenant may find the team's chat, read it and post in it, as a guest (platform
   *  ADR-190). Absent from a core-api that has none. */
  open_chat?: boolean;
  created_at: string;
}

/** A call outcome in the tenant's catalog (ADR-017). Mirrors `DispositionResponse`. */
export interface Disposition {
  id: number;
  tenant_id: number;
  /** Immutable: the contract's `disposition.set`, the CRM's tags and the reports carry it. */
  slug: string;
  label: string;
  /** What choosing it does to the lead: `terminal`, `callback`, `retry` or `hold`. */
  kind: string;
  category: string | null;
  backoff_seconds: number | null;
  hold_seconds: number | null;
  /** The campaign this outcome hands the lead to; null hands them nowhere (ADR-056). */
  enlist_campaign_id: string | null;
  requires_note: boolean;
  sort_order: number;
  active: boolean;
  /** The stage this outcome puts the lead in (ADR-046); null keeps it. */
  stage?: string | null;
  /** The group the window after a call shows it in, and its colour everywhere (ADR-160,
   *  `OUTCOME_GROUPS` in `lib/dispositions.ts`). An admin sets it. */
  outcome_group: string;
  /** False: the platform records it by itself, and a rep is neither offered it nor allowed it. */
  rep_can_pick: boolean;
  /** The campaign the list was asked for works this outcome's stage on a cadence of its own
   *  (ADR-087), so an end stage does not end the calls. Core-api does not send it yet; until it
   *  does the console works it out where the reader may see the campaign's stage cadences
   *  (`keepsCalling` in `lib/dispositions.ts`). */
  works_stage?: boolean;
  /** A booking outcome's no-show limit (ADR-181): after this many of its bookings missed since the
   *  lead last arrived in the outcome's stage, `no_show_outcome` is set. Null: nothing is counted. */
  no_show_limit?: number | null;
  no_show_outcome?: string | null;
}

/** An uploaded dial list, and what its import made of the file. Mirrors `LeadListResponse`. */
export interface LeadList {
  id: number;
  campaign_id: string;
  name: string;
  source: string;
  /** `importing` until the background import writes its counts, then `ready` or `failed`. */
  status: string;
  total_rows: number;
  imported: number;
  duplicates: number;
  skipped: number;
  /** Why rows were skipped: reason → rows. */
  skip_reasons: Record<string, number>;
  /** Leads made without a value the file gave that could not be read: `bad_state`,
   *  `bad_language` → leads (ACL-490). Absent from a server older than it. */
  warnings?: Record<string, number>;
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

// --- webhooks (ADR-022) ----------------------------------------------------------------------------
// Mirrors `WebhookEndpointResponse`, `WebhookSubscriptionResponse`, `WebhookDeliveryResponse` and
// `WebhookEventTypeResponse` in core-api's `schemas.py`.

/** A URL of the tenant's that the platform sends events to. Its signing secret is never read back. */
export interface WebhookEndpoint {
  id: number;
  tenant_id: number;
  name: string;
  kind?: "http" | "slack";
  slack_connection_id?: number | null;
  slack_channel_id?: string | null;
  slack_channel_name?: string | null;
  url: string | null;
  /** POST | PUT | PATCH */
  method: string;
  /** Sent with every delivery, values included: a receiver's own API key, say. */
  headers: Record<string, string>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/** The answer to a create: the one time the secret is shown. */
export interface WebhookEndpointCreated extends WebhookEndpoint {
  secret: string;
}

/** One event type an endpoint hears, from every campaign or from one. */
export interface WebhookSubscription {
  id: number;
  endpoint_id: number;
  tenant_id: number;
  event_type: string;
  /** `tenant`: every campaign's events. `campaign`: only `source_id`'s. */
  source_type: string;
  source_id: string | null;
  /** {dotted path into the event: the value it must hold}, every one of them. */
  conditions: Record<string, string | number | boolean | null>;
  /** `event`: the event as JSON. `template`: `template` rendered over it. */
  post_in?: "thread" | "channel" | null;
  body_mode: string;
  template: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/** One event on its way to one subscription's endpoint. A page of the log carries no events. */
export interface WebhookDelivery {
  id: number;
  subscription_id: number;
  endpoint_id: number;
  tenant_id: number;
  event_id: string;
  slack_message_url?: string | null;
  slack_ts?: string | null;
  slack_thread_ts?: string | null;
  slack_channel_id?: string | null;
  event_type: string;
  /** pending | delivered | failed | dead */
  status: string;
  attempts: number;
  next_attempt_at: string | null;
  last_attempt_at: string | null;
  last_status: number | null;
  last_error: string | null;
  replay_requested_at: string | null;
  /** A sample an admin sent to try the endpoint, not an event that happened. */
  test: boolean;
  created_at: string;
  delivered_at: string | null;
}

/** One delivery read on its own: its event as it was queued, until the retention period empties it. */
export interface WebhookDeliveryDetail extends WebhookDelivery {
  payload: Record<string, unknown> | null;
}

/** An event type a subscription can name, with the contract's own example of it. */
export interface WebhookEventType {
  builtin_message?: boolean;
  type: string;
  sample: Record<string, unknown> | null;
}

/**
 * A field of the contact form a subscription can name, as `context.lead.fields.<key>`, with an example
 * answer as the form keeps one: what a test sends for it. Null when the field's settings do not fit it.
 */
export interface WebhookField {
  key: string;
  title: string;
  type: string;
  example: unknown;
}

// --- triggers (ADR-021) ----------------------------------------------------------------------------
// Mirrors `TriggerResponse`, `TriggerCreated`, `TriggerSecretResponse`, `TriggerInboxRowResponse` and
// `TriggerInboxDetailResponse` in core-api's `schemas.py`.

/** A URL another system calls when something happens there, and what the platform does about it. */
export interface Trigger {
  id: number;
  tenant_id: number;
  name: string;
  description: string | null;
  /** capture | lead.hold | lead.release | lead.delist | lead.dnc | lead.enlist | message.received |
   *  message.status | message.send */
  action: string;
  /** Where a sender posts, after the API's own address: `/hooks/t/{key}`. */
  path: string;
  /** `header`: the secret in `secret_header`. `basic`: the secret as the password of HTTP basic
   *  credentials, which a sender like Twilio takes in the URL it calls. */
  secret_kind: string;
  /** The header the secret travels in. The secret itself is never read back. */
  secret_header: string;
  /** A dotted path into a request per field, and `idempotency` and `attributes`. */
  mapping: Record<string, unknown>;
  options: Record<string, unknown>;
  campaign_id: string | null;
  lead_source: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  last_received_at: string | null;
}

/** The answer to a create: the one time the secret is shown. */
export interface TriggerCreated extends Trigger {
  secret: string;
}

/** The answer to a rotation. The old secret no longer works. */
export interface TriggerSecret {
  secret: string;
  secret_header: string;
}

/** One request a trigger received. A page of the inbox carries no request body. */
export interface TriggerInboxRow {
  id: number;
  trigger_id: number;
  idempotency_key: string;
  received_at: string;
  /** received | applied | rejected | failed */
  status: string;
  attempts: number;
  /** What the action did, or why it did nothing. */
  result: Record<string, unknown> | null;
  error: string | null;
  applied_at: string | null;
}

/** One request read on its own: its body as it arrived, until the retention period empties it. */
export interface TriggerInboxDetail extends TriggerInboxRow {
  raw: Record<string, unknown> | null;
}

/** A lead on a campaign, and where it stands with the dialer. Mirrors `CampaignLeadResponse`. */
export interface CampaignLead {
  id: number;
  lead_id: string;
  list_id: number | null;
  state: string;
  priority: number;
  attempts: number;
  next_attempt_at: string | null;
  last_attempt_at: string | null;
  last_disposition: string | null;
  last_outcome: string | null;
  offered_to: string | null;
  assigned_rep: string | null;
  hold_until: string | null;
  block_reason: string | null;
  retire_at: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  created_at: string;
  /** The row's place on the campaign's cadence (ADR-066), and the words for why. */
  priority_reason?: string | null;
  next_reason?: string | null;
  cadence_cycle?: number;
  cadence_step?: number;
  technical_retries?: number;
  step_not_after?: string | null;
}

// --- cadences (ADR-066) -----------------------------------------------------------------------------
//
// Mirrors the cadence schemas in core-api's `schemas.py` and the body `services/shared/cadence_body.py`
// validates in `platform`.

/** What a call or a redial does when a voicemail answers it (ADR-103 in platform): hang up, have
 *  a rep leave the message (`leave_message`), or have the dialer leave it with no rep, after a
 *  wait (`leave_message_auto`, ADR-109). */
export type OnVoicemail = "hang_up" | "leave_message" | "leave_message_auto";

/** A redial of a call nobody answered: made at once, for the same rep (ADR-103). */
export interface CadenceRedial {
  did_strategy?: string;
  on_voicemail?: OnVoicemail;
  /** A voicemail library slug; null for the message for the lead's stage and language. */
  voicemail_message?: string | null;
  /** For `leave_message_auto`: the seconds between the voicemail being heard and the message. */
  voicemail_after_seconds?: number;
}

/** What a step of a cycle is: a call a rep takes, or a text nobody has to be free for (ADR-135 in
 *  platform). */
export type StepChannel = "call" | "sms";

/** One step of a cycle besides its wait and its number: whether it repeats, and for a call what
 *  it does on a voicemail and its redials (ADR-103), for a text its words (ADR-135). The name is
 *  from when every step was a call. */
export interface CadenceCall {
  /** Absent is a call. */
  channel?: StepChannel;
  only_once?: boolean;
  on_voicemail?: OnVoicemail;
  voicemail_message?: string | null;
  voicemail_after_seconds?: number;
  redials?: CadenceRedial[];
  /** A text's words, with fields written as a greeting writes them: `{{lead.first_name|there}}`. */
  text?: string | null;
  /** The same in Spanish, for a lead whose language is Spanish. */
  text_es?: string | null;
}

export interface CadenceCycle {
  attempts: number;
  between_attempts_seconds?: number[];
  after_cycle_seconds: number;
  initial_delay_seconds?: number;
  did_strategy?: string[];
  voicemail_after_attempt?: number | null;
  /** One entry per step of the cycle, in order, or none: every step a call at its defaults. */
  calls?: CadenceCall[];
}

export interface CadencePhase {
  name: string;
  ends_days_after_enrollment?: number | null;
  priority_band: number;
  age_decay_per_day?: number;
  priority_floor?: number | null;
  not_after_seconds?: number | null;
  /** "Start rounds in the lead's calling window" (ADR-156); absent is off. */
  prefer_window?: boolean;
  cycle: CadenceCycle;
}

/** The body an admin saves as a version. Loosely typed: the server validates it. */
export interface CadenceBody {
  clock?: string;
  phases: CadencePhase[];
  opening?: Record<string, unknown>;
  limits?: {
    total_attempts?: number;
    expire_days_after_enrollment?: number | null;
    /** Calls to one number in a day, from any campaign in any mode, a hand dial too; null is no
     *  cap (ADR-067, the meaning ADR-064 gave the campaign's own cap). */
    daily_calls_per_number?: number | null;
    /** Texts a cadence sends one number in a day, 1 to 10; absent is the server's two (ADR-135).
     *  A cadence always has a cap on texts: the server refuses null, and reads one saved before
     *  that as two. */
    daily_texts_per_lead?: number | null;
  };
  technical?: {
    retry_seconds?: number;
    max_retries?: number;
    technical_causes?: string[];
    invalid_number_causes?: string[];
    invalid_stops_after?: number;
  };
  on_outcome?: {
    not_worked_seconds?: number;
    abandoned_counts?: boolean;
    manual_dial_counts?: boolean;
    inbound_counts?: boolean;
    callback_default_seconds?: number;
    /** How long a callback waits for the rep it is kept for, from its time (ADR-157 in platform). */
    callback_kept_for_seconds?: number;
    /** How long a callback is worked after its time (ACL-661): the phase's own calls, redials and
     *  waits until then. Absent or null: just its call and redials. */
    callback_window_seconds?: number | null;
    [key: string]: unknown;
  };
  /** What the cadence does with a lead when it runs out with nobody reached (ADR-155): the
   *  outcome it sets, as the system. Absent or null sets nothing. */
  on_end?: { outcome?: string | null };
  priority?: {
    callback?: number;
    about_to_sign?: number;
    mid_cycle?: number;
    reply_received?: number;
    initial_fresh?: number;
  };
}

export interface CadenceVersion {
  id: number;
  version_n: number;
  body: CadenceBody;
  unsupported_in_v1: string[];
  note: string | null;
  created_by: number | null;
  created_at: string;
}

export interface Cadence {
  id: number;
  tenant_id: number;
  slug: string;
  name: string;
  active: boolean;
  current_version: CadenceVersion | null;
  /** The fields its texts use that a text can no longer use (ADR-135): one of the tenant's own
   *  that was unticked or archived since. Absent from a core-api that predates texts. */
  unusable_text_fields?: string[];
  versions: number;
  campaigns: number;
  created_at: string;
  updated_at: string;
}

/** The cadence a campaign runs, as the campaign shows it. */
export interface CadenceRef {
  id: number;
  slug: string;
  name: string;
  version_n: number | null;
  unsupported_in_v1: string[];
  problem: string | null;
}

/** One stage the campaign gives a cadence of its own (ADR-087). */
export interface CampaignStageCadence {
  stage: string;
  cadence_id: number;
  /** Null for a cadence that no longer exists. */
  cadence: CadenceRef | null;
}

export interface CadenceCampaignRef {
  id: string;
  name: string;
  slug: string;
}

export interface LeadCadenceDecision {
  id: number;
  at: string;
  kind: string;
  trigger_key: string;
  from_state: string | null;
  to_state: string | null;
  cycle: number | null;
  step: number | null;
  next_attempt_at: string | null;
  step_not_after: string | null;
  priority: number | null;
  reason: string | null;
  /** Always false since ADR-067; a decision written while a cadence ran in shadow keeps its flag. */
  shadow: boolean;
  /** What the decision names beyond its reason: a stage switch's `from`, `to` (`slug@version`)
   *  and `stage` (ADR-087). Absent from a core-api that predates it. */
  detail?: Record<string, unknown> | null;
}

export interface LeadCadenceRequest {
  id: number;
  kind: string;
  until: string | null;
  reason: string | null;
  requested_by: number | null;
  requested_at: string;
  applied_at: string | null;
  applied_result: string | null;
}

/** The lead's row on one campaign, read as the dialer reads it. */
export interface LeadCadenceMembership {
  campaign_lead_id: number;
  campaign_id: string;
  campaign_name: string | null;
  /** `slug@version`, or null on a campaign without a cadence. */
  cadence: string | null;
  /** The cadence's name; absent from a core-api that predates it. */
  cadence_name?: string | null;
  /** The stage the campaign gives this cadence to (ADR-087); null on the campaign's default. */
  cadence_stage?: string | null;
  state: string;
  dial: string | null;
  /** With `dial` `needs_outcome`, the rep it waits for (`LeadCardCampaign.outcome_rep`). */
  outcome_rep?: string | null;
  next_at: string | null;
  next_attempt_at: string | null;
  next_reason: string | null;
  step_not_after: string | null;
  priority: number;
  priority_reason: string | null;
  cycle: number;
  step: number;
  /** What the step the row waits for is; absent from a core-api that predates texts (ADR-135). */
  next_channel?: StepChannel | null;
  attempts: number;
  technical_retries: number;
  held_by: string | null;
  phase: string | null;
  expires_at: string | null;
  assigned_rep: string | null;
  last_transition_at: string | null;
  pending_requests: LeadCadenceRequest[];
  decisions: LeadCadenceDecision[];
  /** The outcome the row's cadence sets when it runs out (ADR-155); absent from a core-api that
   *  predates the setting, null for none. */
  on_end_outcome?: string | null;
  on_end_outcome_label?: string | null;
  /** What the cadence set when it ran out on this row, the newest time; null if it never did. */
  ran_out?: LeadCadenceRanOut | null;
}

/** `result`: null while core-api has not recorded it; `recorded`; `moved_on` (the lead was in
 *  another stage by then); `not_offered`; `exit`; `no_lead`. */
export interface LeadCadenceRanOut {
  outcome: string;
  outcome_label: string | null;
  requested_at: string;
  result: string | null;
}

export interface LeadCadence {
  lead_id: string;
  memberships: LeadCadenceMembership[];
}

export interface CadenceActionResult {
  action: string;
  affected: number;
  requests: LeadCadenceRequest[];
}

export interface DialProblem {
  rep: string;
  reason: string;
  first_seen: string;
  last_seen: string;
  count: number;
}

export interface CadenceWorkRow {
  campaign_lead_id: number;
  lead_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  state: string;
  dial: string | null;
  next_attempt_at: string | null;
  next_reason: string | null;
  step_not_after: string | null;
  priority: number;
  priority_reason: string | null;
  cycle: number;
  step: number;
  attempts: number;
  technical_retries: number;
  hold_until: string | null;
  hold_reason: string | null;
  assigned_rep: string | null;
}

export interface CadenceWork {
  campaign_id: string;
  cadence: string | null;
  problem: string | null;
  buckets: { bucket: string; count: number }[];
  bucket: string | null;
  rows: CadenceWorkRow[];
  dial_problems: DialProblem[];
  /** The reps on the campaign's line the dialer gives nothing now, and why (ACL-568). Absent from
   *  an older core-api. */
  no_calls_now?: CadenceNoCalls[];
}

/** A Ready rep the dialer gives nothing now, and why: what their phone says (ACL-568). */
export interface CadenceNoCalls {
  rep: string;
  reason: string;
  next_due_at: string | null;
}

/** A campaign's abandon rate over the 30 days to now against the abandonment rule
 *  (`GET /campaigns/{id}/abandonment`, `GET /abandonment`; platform ADR-206). Over the campaign's
 *  preview and power dials: abandoned over live answers. Shown only, never acted on. */
export interface CampaignAbandonment {
  campaign_id: string;
  campaign_name: string | null;
  since: string;
  until: string;
  /** The legal ceiling (0.03). */
  limit: number;
  live_answers: number;
  abandoned: number;
  abandon_rate: number | null;
  over_limit: boolean;
}

export interface CadenceMigrateRow {
  campaign_lead_id: number;
  lead_id: string;
  state: string;
  attempts: number;
  cycle: number;
  step: number;
  phase: string | null;
  priority: number;
  priority_reason: string | null;
  on_cadence: boolean;
}

export interface CadenceMigrate {
  campaign_id: string;
  cadence: string | null;
  total: number;
  rows: CadenceMigrateRow[];
}

export const NUMBER_STATUSES = ["available", "pending_approval", "assigned"] as const;
export type NumberStatus = (typeof NUMBER_STATUSES)[number];

export const NUMBER_STATUS_LABELS: Record<NumberStatus, string> = {
  available: "Available",
  pending_approval: "Requested",
  assigned: "Assigned",
};

/** A number in the platform's pool — the asset, and who holds it. */
export interface PhoneNumber {
  id: number;
  number: string;
  country: string;
  region: string | null;
  locality: string | null;
  area_code: string | null;
  number_type: string;
  capabilities: Record<string, boolean>;
  provider: string;
  notes: string | null;
  status: NumberStatus;
  tenant_id: number | null;
  tenant_name: string | null;
  enabled: boolean;
  /** The routing row, once assigned. */
  did_id: number | null;
  /** The request awaiting a decision, while there is one. */
  pending_order_id: number | null;
  created_at: string;
  updated_at: string;
}

export const ORDER_STATUSES = ["pending_approval", "approved", "rejected", "expired", "cancelled"] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending_approval: "Awaiting approval",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
  cancelled: "Cancelled",
};

/** A tenant's request for one number, kept after it is decided. */
export interface NumberOrder {
  id: number;
  phone_number_id: number;
  number: string;
  tenant_id: number;
  tenant_name: string | null;
  requested_by_user_id: number | null;
  requested_by_email: string | null;
  status: OrderStatus;
  note: string | null;
  requested_at: string;
  expires_at: string | null;
  decided_at: string | null;
  decided_by_user_id: number | null;
  decided_by_email: string | null;
  reject_reason: string | null;
  did_id: number | null;
}

export const ROUTE_TYPES = ["campaign", "queue", "extension", "room", "hangup"] as const;
export type RouteType = (typeof ROUTE_TYPES)[number];

/** The routes that name one rep. They differ in where the caller ends up. */
export const ENDPOINT_ROUTES: readonly RouteType[] = ["extension", "room"];

export const ROUTE_LABELS: Record<RouteType, string> = {
  campaign: "Campaign",
  queue: "Queue",
  extension: "Rep's phone",
  room: "Rep's room",
  hangup: "Hang up",
};

export const ROUTE_DESCRIPTIONS: Record<RouteType, string> = {
  campaign: "Park the caller for the campaign's inbound queue, tagged with the campaign.",
  queue: "Park the caller for a named ACD queue.",
  extension: "Ring one rep's phone. The call fails if they are not signed in.",
  room: "Ring the rep's phone. They answer into a room of the call's own, where they can hold the caller, bring colleagues in and hand the call over.",
  hangup: "Play an announcement, if any, then hang up.",
};


/** A number a tenant holds, and what a call to it does. Mirrors `DidResponse`. */
export interface Did {
  id: number;
  number: string;
  description: string | null;
  /** `null` = not routed yet: an approved number nobody has configured. */
  action_type: RouteType | null;
  /** The queue name for `queue`; the sound file for `hangup`. */
  action_target: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  campaign_slug: string | null;
  campaign_queue_name: string | null;
  sip_endpoint_id: number | null;
  sip_endpoint_extension: string | null;
  sip_endpoint_domain_name: string | null;
  sip_endpoint_user_name: string | null;
  /** A campaign route that names a rep (ADR-037): whether that rep still staffs the campaign.
   *  False: the number rings the campaign's line like its own. Null: no rep named. */
  rep_on_campaign?: boolean | null;
  /** The address of the open invitation holding this unrouted number for its rep (ACL-176). The
   *  platform refuses to route it until they accept or the invitation is deleted. */
  held_for?: string | null;
  /**
   * Skills every call to this number needs, on top of the line's own (ADR-015): a Spanish line is
   * a number that needs `spanish`. Only `campaign` and `queue` routes reach the ACD, so the server
   * keeps tags on those two and clears them on the rest.
   */
  routing_tags: SkillRequirement[];
  tenant_id: number | null;
  tenant_name: string | null;
  phone_number_id: number | null;
  enabled: boolean;
  /** Inbound only (platform ADR-202): it still rings where it is routed, and no call or first
   *  text presents it. Since when, and why. Absent from a core-api before it. */
  inbound_only_at?: string | null;
  inbound_only_reason?: InboundOnlyReason | null;
  created_at: string;
  updated_at: string;
}

/** Why a number is inbound only: flagged as spam by hand, or replaced on its campaign. */
export type InboundOnlyReason = "flagged" | "replaced";

/** A number on a campaign's Numbers tab (ACL-629). Its role is read from what exists: `primary`
 *  is the campaign's caller id, `rep` a campaign route naming a rep (ADR-037), `local` one naming
 *  none (ADR-069). */
export interface CampaignNumber {
  did_id: number;
  number: string;
  role: "primary" | "local" | "rep";
  area_code: string | null;
  region: string | null;
  /** A campaign route to this campaign. The primary may reach it some other way; false says so. */
  routed_here: boolean;
  sip_endpoint_id: number | null;
  rep_name: string | null;
  rep_on_campaign: boolean | null;
  enabled: boolean;
  inbound_only_at: string | null;
  inbound_only_reason: InboundOnlyReason | null;
  /** Calls it was presented on since midnight on the campaign's clock, from any campaign. */
  calls_today: number;
}

export interface CampaignNumbers {
  campaign_id: string;
  caller_id_number: string | null;
  rep_number_caller_id: boolean;
  numbers: CampaignNumber[];
}

/**
 * One line saying where a call to this number goes. The table and the route dialog both show
 * it, and the server's shape (`action_type` + three targets) is not something to read at a
 * glance: "Rep · Ana (1001@reps.example)" is.
 */
export function routeSummary(did: Pick<
  Did,
  | "action_type" | "action_target" | "campaign_name" | "campaign_queue_name"
  | "sip_endpoint_extension" | "sip_endpoint_domain_name" | "sip_endpoint_user_name"
> & { sip_endpoint_id?: number | null }): string {
  switch (did.action_type) {
    case "campaign": {
      const name = did.campaign_name ?? "unknown campaign";
      if (!did.campaign_queue_name) return `${name} (no inbound queue — calls will hang up)`;
      // A number that names one of the campaign's reps rings them first (ADR-037).
      if (did.sip_endpoint_id != null) {
        return `${name} → ${did.sip_endpoint_user_name ?? did.sip_endpoint_extension ?? "a rep"}`;
      }
      return `${name} → queue ${did.campaign_queue_name}`;
    }
    case "queue":
      return `Queue ${did.action_target ?? "?"}`;
    // Both routes name the same thing, the rep: the room a `room` call lives in is made for that
    // call and has no name worth printing (ADR-033). The route label says which route it is.
    case "extension":
    case "room": {
      const ext = did.sip_endpoint_extension
        ? `${did.sip_endpoint_extension}@${did.sip_endpoint_domain_name ?? "?"}`
        : "endpoint gone — calls will hang up";
      return did.sip_endpoint_user_name ? `${did.sip_endpoint_user_name} (${ext})` : ext;
    }
    case "hangup":
      return did.action_target ? `Play ${did.action_target}, then hang up` : "Hang up";
    default:
      return "Not routed";
  }
}

/**
 * Whether a route as stored can still take a call — mirrors what the xml gateway checks at
 * call time. The console flags these so an admin finds out before a caller does.
 */
export function routeIsBroken(
  did: Pick<Did, "action_type" | "campaign_queue_name" | "sip_endpoint_id" | "campaign_id">,
): boolean {
  if (did.action_type === "campaign") return !did.campaign_id || !did.campaign_queue_name;
  if (did.action_type && (ENDPOINT_ROUTES as readonly string[]).includes(did.action_type)) {
    return did.sip_endpoint_id === null;
  }
  return false;
}

/**
 * Parse a pasted block of numbers — one per line, or separated by commas — into the strings
 * the server normalises. Blank lines and duplicates are dropped here so the batch the server
 * refuses for a repeat is one the user actually typed twice, not a paste artefact.
 */
export function splitNumbers(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(/[\n,;]+/)) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    // The same approximation of the server's E.164 rule: digits only, and a bare ten-digit US
    // number is the same line as its +1 spelling.
    const digits = trimmed.replace(/\D/g, "");
    const key = digits.length === 10 ? `1${digits}` : digits;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

// --- calls (CDRs) ------------------------------------------------------------------------------
//
// Mirrors `CdrResponse`, `CdrDetailResponse` and `CdrSummaryResponse` in core-api's `schemas.py`.
//
// One row is one **leg**, which is what the switch writes. A conversation between a caller and a
// rep is usually two rows, and they share no id in `cdrs` — so the console counts legs and says
// so, rather than inventing a join key the switch never gave it.

/** What the dialer stamped on the leg. `other` is the fallback for a leg it never touched. */
export const CALL_LEGS = ["lead", "rep", "room", "inbound", "sound_test", "other"] as const;
export type CallLeg = (typeof CALL_LEGS)[number];

export const CALL_LEG_LABELS: Record<CallLeg, string> = {
  lead: "Lead",
  rep: "Rep",
  room: "Room",
  inbound: "Inbound",
  sound_test: "Sound test",
  other: "Unattributed",
};

export const CALL_LEG_DESCRIPTIONS: Record<CallLeg, string> = {
  lead: "The dialled party on an outbound campaign call.",
  rep: "A rep's own leg.",
  room: "A leg inside a rep's conference room.",
  inbound: "A caller arriving on a DID.",
  sound_test: "A rep checking their microphone and speaker from the audio setup dialog.",
  other: "The dialer never stamped this leg — today that is every call a rep places by hand.",
};

/**
 * The reader's own legs that are not calls with anybody, for `exclude_leg` on a personal list:
 * their line into their own conference room, and their sound tests (ADR-062 in `platform`).
 */
export const OWN_NON_CALL_LEGS = "room,sound_test";

/** One finished leg, as the calls table shows it. */
export interface Cdr {
  id: number;
  channel_uuid: string;
  leg: string | null;
  direction: "inbound" | "outbound" | null;

  caller_number: string | null;
  caller_name: string | null;
  callee_number: string | null;
  callee_name: string | null;

  domain: string | null;
  did: string | null;
  campaign_id: string | null;
  /** Resolved server-side. Null for a campaign deleted since the call — the id survives. */
  campaign_name: string | null;
  lead_id: string | null;
  /** The contact this leg was with, resolved server-side from `lead_id`. Neither name column can
   *  stand in for it: the trunk sends no caller-id name, so without this a call from a lead the
   *  firm has held for months reads as a bare number. Null on a leg that names no lead, on one
   *  whose lead has been deleted since, and where the lead has no name. */
  lead_name: string | null;
  /** Which of this leg's two numbers is the contact's, so the name goes against the right one.
   *  Not derivable here: the ACD rings the rep WITH the lead as the caller, so the rep's leg of
   *  an inbound call is an outbound channel carrying the lead in `caller_number`. */
  lead_side: "caller" | "callee" | null;
  /** What the leg was to the ACD: `inbound` (a caller on a line), `outbound` (a lead the dialer
   *  reached, on the rep's leg too), `handoff`; null on a leg that never met the ACD. */
  kind: string | null;
  /** How a dialer call was placed — `manual`, `preview` or `power` — on the rep's leg as on the
   *  lead's; null on every other call. */
  dial_mode: string | null;
  /** How the call came about (ACL-649, ADR-212 in `platform`), from core-api, so a table and its
   *  export cannot disagree. Null when the leg does not say. */
  call_type?: "dialer" | "manual" | "inbound" | "handoff" | "internal" | null;
  /** Which call of its cadence round a dialer call was, counted from 1, and which attempt of it
   *  (ADR-105 in `platform`): `redial_n` is 0 for the call and k for its k-th redial, of
   *  `redials_of`. On the lead's leg and on the leg of the rep the lead was brought to.
   *  `redial_n` is null on a leg of no cadence call (an inbound call, a number dialed by hand),
   *  and `redials_of` on a dial made before it was recorded. Absent from an older core-api. */
  step_n?: number | null;
  redial_n?: number | null;
  redials_of?: number | null;

  amd_result: string | null;
  human_answer: boolean;
  abandoned: boolean;
  /**
   * The caller was blocked on this call (ADR-159 in platform): `number`, turned away with a busy
   * tone, or `hidden`, told hidden calls are not taken. The switch turned them away, or a rep
   * blocked them as the call rang. Absent from a core-api that predates it.
   */
  blocked?: "number" | "hidden" | null;
  /**
   * The recording this leg is part of, off the `acl_recording_id` channel variable. One file is
   * captured per call — on the A-leg — and both parties' legs are stamped with its id, so on a
   * bridged call this names the OTHER leg's uuid rather than this one's.
   *
   * Still not the field to test for "is there audio": it says the switch started a recording,
   * not that the file reached the bucket or that this reader may hear it. That is
   * `has_recording`.
   */
  recording_id: string | null;
  /** Whether the signed-in user can play a recording of this leg. Scoped server-side, so it
   *  means "yours to hear" and not merely "it exists" — a play button rendered on this will
   *  never 404. */
  has_recording: boolean;
  /** Whether this call has a stored transcript — live, from a rep's Transcribe toggle, or from
   *  the whole-file pass over its recording. Unlike `has_recording` this is not an entitlement
   *  answer: a transcript belongs to the call rather than to one leg's endpoint, so anyone who
   *  can see this row can read it. A job that has been queued and has not finished is `false` —
   *  the reader wants to know there is nothing to read yet, not that something was ordered. */
  has_transcript: boolean;

  start_time: string | null;
  answer_time: string | null;
  end_time: string | null;
  duration_seconds: number;
  /** Billed seconds. `null` answer_time means this is 0 and the leg was never picked up. */
  billsec_seconds: number;

  hangup_cause: string | null;
  /**
   * The caller's stay in an ACD line, on a leg that waited in one (ADR-025): the newest stay's line,
   * and how it ended — CONNECTED, DROP, ABANDONED, TIMEOUT, REHOMED or REMOVED; after a handoff
   * nobody picked up, the stay a rep took before it. Null on a leg that never waited and while the
   * stay is open; absent from a platform that predates the columns.
   */
  queue_name?: string | null;
  queue_outcome?: string | null;
  /** The conference room the leg was in: `direct-<uuid>` for a call on a rep's own number,
   *  `call-<uuid>` for one the ACD connected. The one field that relates the legs of a call to
   *  each other; null on a leg that was never in one. */
  room?: string | null;
  /** For a leg a rep brought into that room: who asked (their extension) and how — `consult`,
   *  a word first with the caller held, or `add`, straight onto the call. */
  invited_by?: string | null;
  invite_mode?: string | null;
  hold_count: number;
  hold_seconds: number;
  transfer_count: number;
  /**
   * How the leg sounded, as the switch measured it.
   *
   * `audio_in_mos` tops out at **4.5**, not 5 — that is the ceiling for the codecs this switch
   * negotiates, so a meter scaled to 5 draws every healthy call at 90%. Null is not a bad score:
   * a third of the table carried no RTP at all (a leg that never answered, a carrier leg nothing
   * was measured on), and `lib/call-quality.ts` draws those as an absence.
   */
  audio_in_mos: number | null;
  audio_in_quality_percentage: number | null;
  audio_in_jitter_loss_rate: number | null;
  audio_in_jitter_burst_rate: number | null;
  audio_in_packet_count: number | null;
  audio_in_skip_packet_count: number | null;
  audio_out_packet_count: number | null;
  audio_rtcp_packet_count: number | null;
  read_codec: string | null;
  write_codec: string | null;
  read_rate: number | null;
  write_rate: number | null;

  created_at: string;
}

/** How a colleague was brought into a call, as the invite stamped it on their leg. */
export const INVITE_MODE_LABELS: Record<string, string> = {
  consult: "for a word first",
  add: "to join the call",
};

/** "Brought in by 1001 for a word first", or null for a leg nobody brought in. */
export function broughtInBy(cdr: Pick<Cdr, "invited_by" | "invite_mode">): string | null {
  if (!cdr.invited_by) return null;
  const how = cdr.invite_mode ? (INVITE_MODE_LABELS[cdr.invite_mode] ?? cdr.invite_mode) : null;
  return how ? `Brought in by ${cdr.invited_by} ${how}` : `Brought in by ${cdr.invited_by}`;
}

/** Everything else the switch reported about one leg — the support view. */
/** Another leg of the same call. `id` is what opens it. */
export interface CdrOtherLeg {
  id: number;
  channel_uuid: string;
  leg: string | null;
  /** How it was found to be the same call: the per-call conference both were in, a direct bridge,
   *  or one recording covering both. Shown, because a reader deciding whether two rows are really
   *  one call wants to know which fact says so. */
  relation: "room" | "bridge" | "recording";
  caller_number: string | null;
  callee_number: string | null;
  endpoint_user: string | null;
  start_time: string | null;
  billsec_seconds: number;
}

export interface CdrDetail extends Cdr {
  attempt_n: number | null;
  cadence: string | null;
  step_n: number | null;
  step_id: string | null;

  caller_network_ip: string | null;
  bridge_time: string | null;
  last_application: string | null;
  last_application_args: string | null;

  read_codec: string | null;
  write_codec: string | null;
  read_rate: number | null;
  write_rate: number | null;
  audio_in_quality_percentage: number | null;
  audio_in_jitter_loss_rate: number | null;
  audio_in_jitter_burst_rate: number | null;
  audio_in_packet_count: number | null;
  audio_in_skip_packet_count: number | null;
  audio_out_packet_count: number | null;
  audio_rtcp_packet_count: number | null;

  /** Which side put the phone down, as the endpoint module recorded it on this channel:
   *  `recv_bye` / `send_bye` once the call was up, `recv_cancel` / `send_cancel` before it was
   *  answered, `recv_refuse` / `send_refuse` where it was rejected. `recv_*` is the far end of
   *  THIS leg; `send_*` is the switch ending it, which on a two-leg call means the other party
   *  went first. Null where the module recorded neither. */
  hangup_disposition: string | null;
  /** The other legs of this call, newest first, scoped exactly like the row itself. */
  other_legs: CdrOtherLeg[];

  raw_payload: Record<string, unknown> | null;
}

/** Totals for the filter the table is showing — never for the visible page. */
export interface CdrSummary {
  calls: number;
  answered: number;
  /** 0–1, or `null` when there were no calls: a rate over zero is not zero. */
  answer_rate: number | null;
  /** Legs the dialer abandoned under safe harbour. */
  abandoned: number;
  /**
   * Legs whose queue stay ended with the caller hanging up while waiting, and with no rep before the
   * line's maximum wait. A lead the dialer abandoned is counted in `abandoned` only, so the three add
   * up with no leg counted twice. Absent from a platform that predates them.
   */
  queue_abandoned?: number;
  queue_timed_out?: number;
  /** The switch's reading of the channel, not the reader's: an `inbound` channel is one a rep's
   *  own phone dialled in with, which is a call they *placed*. The two counts below are the
   *  reader's own split and are what a personal history shows. */
  inbound: number;
  outbound: number;
  /** How many of these legs the signed-in user placed, and how many they were rung for. Both 0
   *  for an account with no phone. Mirrors `mine_direction`, so a tile and the filter under it
   *  cannot describe different populations. */
  placed_by_me: number;
  received_by_me: number;
  talk_seconds: number;
  avg_talk_seconds: number | null;
  by_hangup_cause: Record<string, number>;
}

/**
 * How a hangup cause should read on screen. Only the ones an operator sees weekly are
 * translated; anything else falls through to the switch's own spelling, which is what they
 * would search for anyway.
 *
 * `tone` is deliberately not "red = bad". `NO_ANSWER` on an outbound dialer is the expected
 * outcome most of the time, and colouring two thirds of a healthy campaign red trains people to
 * ignore the column. Red is reserved for causes that mean something is broken.
 */
export const HANGUP_CAUSES: Record<string, { label: string; tone: "neutral" | "warning" | "danger" }> = {
  NORMAL_CLEARING: { label: "Completed", tone: "neutral" },
  NO_ANSWER: { label: "No answer", tone: "neutral" },
  ORIGINATOR_CANCEL: { label: "Cancelled", tone: "neutral" },
  LOSE_RACE: { label: "Lost race", tone: "neutral" },
  USER_BUSY: { label: "Busy", tone: "warning" },
  CALL_REJECTED: { label: "Rejected", tone: "warning" },
  NO_USER_RESPONSE: { label: "No response", tone: "warning" },
  RECOVERY_ON_TIMER_EXPIRE: { label: "Timed out", tone: "warning" },
  // The cause the ACD hangs up a waiting caller with when it takes them out of a line.
  ALLOTTED_TIMEOUT: { label: "Timed out", tone: "warning" },
  USER_NOT_REGISTERED: { label: "Rep not signed in", tone: "danger" },
  NO_ROUTE_DESTINATION: { label: "No route", tone: "danger" },
  UNALLOCATED_NUMBER: { label: "Unallocated number", tone: "danger" },
  DESTINATION_OUT_OF_ORDER: { label: "Destination down", tone: "danger" },
  NETWORK_OUT_OF_ORDER: { label: "Network down", tone: "danger" },
  SYSTEM_SHUTDOWN: { label: "Switch restarted", tone: "danger" },
};

export function hangupLabel(cause: string | null): string {
  if (!cause) return "—";
  return HANGUP_CAUSES[cause]?.label ?? cause.replaceAll("_", " ").toLowerCase();
}

export function hangupTone(cause: string | null): "neutral" | "warning" | "danger" {
  return cause ? (HANGUP_CAUSES[cause]?.tone ?? "neutral") : "neutral";
}

/**
 * Who the leg was with, from the point of view of this organisation.
 *
 * A CDR names a caller and a callee, but which of those is "the other party" flips with
 * direction — and a table column that sometimes shows the rep and sometimes the customer is a
 * column nobody can scan. Returns both halves so the row can show them in a fixed order.
 */
export function callParties(cdr: Pick<Cdr, "direction" | "caller_number" | "caller_name" | "callee_number" | "callee_name">): {
  from: { number: string; name: string | null };
  to: { number: string; name: string | null };
} {
  return {
    from: { number: cdr.caller_number ?? "—", name: cdr.caller_name },
    to: { number: cdr.callee_number ?? "—", name: cdr.callee_name },
  };
}

// --- reports (ADR-074) ---------------------------------------------------------------------------
//
// Mirrors the `/reports/*` responses in core-api's `schemas.py`. A rate or an average is null over
// an empty denominator; a series lists every bucket, labelled with its start on the report's own
// clock (`2026-09-21T00:00:00-07:00`), which `lib/reports.ts` reads without moving it into the
// browser's zone.

export interface ReportWindow {
  since: string;
  until: string;
  tz: string;
  granularity: "hour" | "day" | "week" | "month";
  weekdays: number[];
  hour_from: number;
  hour_to: number;
  previous_since: string | null;
  previous_until: string | null;
}

export interface LineCounts {
  offered: number;
  answered: number;
  abandoned: number;
  timed_out: number;
  answer_rate: number | null;
  abandon_rate: number | null;
  service_level: number | null;
  wait_avg: number | null;
  talk_total: number;
}

export interface LineSeriesPoint extends LineCounts {
  start: string;
}

export interface LineHeatCell extends LineCounts {
  weekday: number;
  hour: number;
}

export interface WaitBin {
  lo: number;
  hi: number | null;
  answered: number;
  abandoned: number;
  timed_out: number;
}

export interface ReportLineRow extends LineStats {
  campaign_id: string | null;
  campaign_name: string | null;
  purpose: string | null;
  description: string | null;
  /** Gone from the configuration; its history is not. */
  deleted: boolean;
}

export interface SkillRoutingRow extends LineCounts {
  slug: string;
  name: string | null;
  required: number;
  preferred: number;
  relaxed: number;
  answered_by_holder: number;
  answered_by_other: number;
}

export interface RepNumberRow {
  rep: string;
  name: string | null;
  calls: number;
  their_rep: number;
  someone_else: number;
  nobody: number;
  moved_or_open: number;
}

export interface HandoffSummary {
  requested: number;
  connected: number;
  cancelled: number;
  abandoned: number;
  failed: number;
  in_progress: number;
  connect_rate: number | null;
  by_mode: Record<string, number>;
  named_closer: number;
  any_closer: number;
  expired: number;
  wait_avg: number | null;
  wait_p90: number | null;
  by_line: { queue: string; requested: number; connected: number; cancelled: number; abandoned: number; failed: number }[];
}

export interface LineReport {
  window: ReportWindow;
  sla_seconds: number;
  truncated: boolean;
  totals: LineStats;
  previous: LineStats | null;
  lines: ReportLineRow[];
  series: LineSeriesPoint[];
  heatmap: LineHeatCell[];
  waits: WaitBin[];
  abandons: {
    abandoned: number;
    after_a_rep_rang: number;
    before_any_rep_rang: number;
    dropped_as_answered: number;
    timed_out: number;
    wait_avg: number | null;
    wait_p50: number | null;
    wait_p90: number | null;
    by_kind: Record<string, number>;
  };
  skills: SkillRoutingRow[];
  rep_numbers: {
    calls: number;
    their_rep: number;
    someone_else: number;
    nobody: number;
    moved_or_open: number;
    reps: RepNumberRow[];
  };
  handoffs: HandoffSummary;
}

export interface DialCounts {
  attempts: number;
  answered: number;
  contacts: number;
  voicemail: number;
  no_answer: number;
  busy: number;
  failed: number;
  abandoned: number;
  canceled: number;
  other: number;
  in_progress: number;
  counted: number;
  technical_retries: number;
  answer_rate: number | null;
  contact_rate: number | null;
  abandon_rate: number | null;
}

export interface DialingReport {
  window: ReportWindow;
  truncated: boolean;
  totals: DialCounts;
  previous: DialCounts | null;
  series: (DialCounts & { start: string })[];
  heatmap: (DialCounts & { weekday: number; hour: number })[];
  by_mode: (DialCounts & { dial_mode: string; initiated_by: string })[];
  by_campaign: (DialCounts & { campaign_id: string | null; campaign_name: string | null })[];
  /** Per cadence on each campaign (ADR-087); `cadence` null for dials no cadence placed. Absent
   *  from a core-api that predates it. */
  by_cadence?: (DialCounts & { campaign_id: string | null; campaign_name: string | null; cadence: string | null })[];
  cadence_steps: (DialCounts & {
    campaign_id: string | null;
    campaign_name: string | null;
    cadence: string | null;
    cycle: number | null;
    step: number | null;
  })[];
  did_strategies: (DialCounts & { strategy: string })[];
  caller_ids: (DialCounts & { did: string; top_causes: Record<string, number>; low_answer_rate: boolean; last_used: string })[];
  carriers: (DialCounts & { gateways: string[]; label: string; failure_causes: Record<string, number> })[];
  answer_to_rep: { calls: number; avg: number | null; p50: number | null; p90: number | null; max: number | null };
  abandonment: {
    campaign_id: string | null;
    campaign_name: string | null;
    live_answers: number;
    abandoned: number;
    abandon_rate: number | null;
    over_limit: boolean;
  }[];
  abandonment_since: string;
  blocked: Record<string, number>;
  problems: {
    campaign_id: string;
    campaign_name: string | null;
    rep: string;
    rep_name: string | null;
    reason: string;
    first_seen: string;
    last_seen: string;
    count: number;
  }[];
}

export interface OutcomeSummary {
  total: number;
  on_calls: number;
  on_records: number;
  success: number;
  success_rate: number | null;
  calls_served: number;
  dispositioned_rate: number | null;
}

export interface OutcomeCampaignRow {
  campaign_id: string | null;
  campaign_name: string | null;
  total: number;
  success: number;
  success_rate: number | null;
  by_category: Record<string, number>;
  by_disposition: Record<string, number>;
}

export interface OutcomeReport {
  window: ReportWindow;
  truncated: boolean;
  summary: OutcomeSummary;
  previous: OutcomeSummary | null;
  by_disposition: {
    slug: string;
    label: string;
    category: string | null;
    kind: string | null;
    count: number;
    share: number | null;
    talk_avg: number | null;
  }[];
  by_category: Record<string, number>;
  by_campaign: OutcomeCampaignRow[];
  /** Per cadence on each campaign (ADR-087); `cadence` null for a call no cadence placed and for
   *  an outcome set on a record. Absent from a core-api that predates it. */
  by_cadence?: (OutcomeCampaignRow & { cadence: string | null })[];
  series: {
    start: string;
    total: number;
    success: number;
    contact: number;
    no_contact: number;
    compliance: number;
    uncategorized: number;
  }[];
}

export interface ListReport {
  window: ReportWindow;
  as_of: string;
  campaigns: {
    campaign_id: string;
    campaign_name: string | null;
    total: number;
    by_state: Record<string, number>;
    dialable: number;
    due_now: number;
    held: number;
    finished: number;
    blocked: number;
    blocked_reasons: Record<string, number>;
    attempted: number;
    penetration: number | null;
    attempts: { attempts: number; leads: number; or_more: boolean }[];
    added_in_window: number;
    lists: {
      id: number;
      name: string;
      source: string;
      status: string;
      created_at: string;
      imported: number;
      duplicates: number;
      skipped: number;
      by_state: Record<string, number>;
    }[];
  }[];
}

export interface SpeedToLead {
  leads: number;
  dialed: number;
  contacted: number;
  called_in_first: number;
  never_dialed: number;
  first_dial_avg: number | null;
  first_dial_p50: number | null;
  first_dial_p90: number | null;
  first_contact_p50: number | null;
  first_contact_p90: number | null;
  within_5_minutes: number | null;
  bins: { lo: number; hi: number | null; leads: number }[];
}

export interface StageCount {
  stage: string | null;
  group: string;
  count: number;
}

export interface LeadReport {
  window: ReportWindow;
  truncated: boolean;
  speed: SpeedToLead;
  previous: SpeedToLead | null;
  where_now: StageCount[];
  moves: {
    moves: number;
    into: StageCount[];
    reasons: Record<string, number>;
    signed: number;
    days_to_sign_p50: number | null;
    days_to_sign_p90: number | null;
  };
  firms: {
    firm_id: number | null;
    label: string;
    leads: number;
    dialed: number;
    contacted: number;
    contact_rate: number | null;
    working: number;
    scheduled: number;
    signed: number;
    not_qualified: number;
    sign_rate: number | null;
  }[];
  series: { start: string; new: number; signed: number }[];
}

export interface RepReportRow {
  user: string;
  domain: string;
  name: string | null;
  skills: string[];
  calls: number;
  inbound: number;
  outbound: number;
  handoff: number;
  talk_total: number;
  talk_avg: number | null;
  ring_avg: number | null;
  hung_up_by_rep: number;
  hung_up_by_caller: number;
  handed_off: number;
  dropped: number;
  missed_offers: number;
  missed_by_reason: Record<string, number>;
  signed_in_seconds: number;
  first_in: string | null;
  last_out: string | null;
  state_seconds: Record<string, number>;
  break_seconds: Record<string, number>;
  occupancy: number | null;
  after_call_avg: number | null;
  handle_avg: number | null;
  calls_per_hour: number | null;
  outcomes: number;
  success: number;
  success_rate: number | null;
  outcome_rate: number | null;
  outcome_mix: Record<string, number>;
  handoffs_sent: number;
  handoffs_connected: number;
  handoffs_taken: number;
  dials: number;
  dial_contacts: number;
  dialer_calls: number;
  mos_avg: number | null;
  mos_calls: number;
  poor_calls: number;
}

export interface RepTeamSummary {
  reps: number;
  calls: number;
  talk_total: number;
  talk_avg: number | null;
  missed_offers: number;
  signed_in_seconds: number;
  state_seconds: Record<string, number>;
  occupancy: number | null;
  after_call_avg: number | null;
  handle_avg: number | null;
  calls_per_hour: number | null;
  outcomes: number;
  success: number;
  success_rate: number | null;
  handoffs_sent: number;
  handoffs_taken: number;
  dials: number;
  mos_avg: number | null;
  poor_calls: number;
}

export interface RepReport {
  window: ReportWindow;
  truncated: boolean;
  team: RepTeamSummary;
  previous: RepTeamSummary | null;
  reps: RepReportRow[];
  series: { start: string; calls: number; talk_total: number; staffed_seconds: number; break_seconds: number; occupancy: number | null }[];
  heatmap: { weekday: number; hour: number; calls: number; talk_total: number }[];
  timesheet: {
    user: string;
    domain: string;
    name: string | null;
    day: string;
    first_in: string | null;
    last_out: string | null;
    signed_in_seconds: number;
    break_seconds: Record<string, number>;
    calls: number;
    talk_total: number;
  }[];
}

export interface ReportOptions {
  campaigns: { id: string; name: string; purpose: string; status: string; dial_mode: string; firm_id: number | null }[];
  lines: { name: string; description: string | null; campaign_id: string | null; purpose: string | null; enabled: boolean }[];
  reps: { user: string; domain: string; name: string | null; role: string | null; active: boolean; skills: string[] }[];
  /** The tenant's teams (ADR-195 in platform), with how many people are in each now. */
  teams: { id: number; name: string; members: number }[];
  skills: { slug: string; name: string; category: string | null }[];
  firms: { id: number; label: string; code: string; status: string }[];
  dispositions: { slug: string; label: string; category: string | null; kind: string | null; active: boolean }[];
}

// --- calendars (ADR-085 in platform) -------------------------------------------------------------

export type CalendarType = "personal" | "round_robin" | "class" | "collaborative";
export type CalendarDistribution = "availability" | "equal";
export type RescheduleOwner = "keep" | "reassign";
export type StaffPriority = "high" | "medium" | "low";
export type MeetingLocationKind = "phone" | "address" | "custom";
export type TimeFormat = "12h" | "24h";
export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

/** One range of a staff member's hours on one weekday, "HH:MM" on the calendar's clock. The end
 *  is exclusive; "24:00" is the end of the day. */
export interface AvailabilityRange {
  day: Weekday;
  start: string;
  end: string;
}

export interface CalendarStaff {
  user_id: number;
  display_name: string | null;
  email: string;
  /** False once the account is deactivated: kept on the calendar, never booked. */
  user_active: boolean;
  priority: StaffPriority;
  location_kind: MeetingLocationKind;
  location_detail: string | null;
  availability: AvailabilityRange[];
}

/** `GET /calendars/{id}`: a calendar with its staff and their hours. */
export interface Calendar {
  id: number;
  tenant_id: number;
  name: string;
  description: string | null;
  calendar_type: CalendarType;
  active: boolean;
  /** The one calendar every appointment is booked on (ACL-655, ADR-215). */
  is_main: boolean;
  /** Round robin only; null on every other type. */
  distribution: CalendarDistribution | null;
  reschedule_owner: RescheduleOwner | null;
  prefer_contact_owner: boolean;
  /** Class only (ACL-293): contacts one session holds; null on every other type. */
  seats_per_session: number | null;
  duration_minutes: number;
  slot_interval_minutes: number;
  min_notice_minutes: number;
  pre_buffer_minutes: number;
  post_buffer_minutes: number;
  booking_window_days: number;
  timezone: string;
  time_format: TimeFormat;
  locale: string;
  week_starts_on: Weekday;
  staff: CalendarStaff[];
  created_at: string;
  updated_at: string;
}

/** A smart list as `GET /opportunity-lists` returns it (ADR-078). */
export interface OpportunityListRow {
  id: string;
  name: string;
  view: "board" | "list";
  filters: { field: string; values: string[] }[];
  sort: { field: "created" | "name" | "stage" | "phone"; order: "asc" | "desc" };
  fields: string[];
  density: "comfortable" | "compact";
  shared: boolean;
  owner_id: number;
  owner_name: string | null;
  mine: boolean;
  can_edit: boolean;
  created_at: string;
  updated_at: string;
}

/** A time a calendar can book, and who is free for it. Instants are UTC. */
export interface CalendarSlot {
  start: string;
  end: string;
  user_ids: number[];
  /** Class calendars (ACL-293): seats still free in the session at this time. */
  seats_left?: number | null;
}

/** `GET /calendars/{id}/slots`. The days are on the calendar's clock. */
export interface CalendarSlots {
  calendar_id: number;
  timezone: string;
  first_day: string;
  last_day: string;
  slots: CalendarSlot[];
}

export interface Appointment {
  id: number;
  calendar_id: number;
  lead_id: string | null;
  assigned_user_id: number | null;
  title: string;
  starts_at: string;
  ends_at: string;
  status: string;
  location_kind: MeetingLocationKind;
  location_detail: string | null;
  notes: string | null;
  source: "manual" | "conversation" | "api";
  rescheduled_from_id: number | null;
  /** Whether the contact is texted reminders before it (ACL-504): the rep's choice, until it starts. */
  send_reminders: boolean;
  created_at: string;
}

/**
 * How a booking's confirmation text stands (ACL-504): the text's own status as the provider last
 * reported it, or `not_sent`; and why not, as a sentence a rep can act on.
 */
export interface AppointmentConfirmation {
  status: "scheduled" | "queued" | "sent" | "delivered" | "failed" | "not_sent";
  problem: string | null;
  at: string | null;
  /** Booked outside the contact's hours (ACL-630): when the text goes out, their 8 am. Null
   *  otherwise, and absent from an older core-api. */
  send_at?: string | null;
}

/** `POST /calendars/{id}/appointments`: the appointment, and its confirmation when one was asked for. */
export interface AppointmentBooked extends Appointment {
  confirmation: AppointmentConfirmation | null;
}

// --- appointments (ACL-291) ----------------------------------------------------------------------

export type AppointmentStatus = "scheduled" | "confirmed" | "showed" | "no_show" | "cancelled" | "rescheduled";

/** An appointments list row: the appointment and the names it is read by. */
export interface AppointmentListItem extends Appointment {
  calendar_name: string;
  owner_name: string | null;
  contact_name: string | null;
  /** A class session's contacts (ACL-293); 0 on the other types. */
  attendee_count: number;
}

export interface AppointmentContact {
  id: string;
  name: string | null;
  phone: string;
  email: string | null;
  /** Their clock (ACL-521), offered as a zone to reschedule in. Absent from an older core-api. */
  timezone?: string | null;
}

export interface AppointmentPerson {
  id: number;
  name: string;
  email: string | null;
}

export interface AppointmentEvent {
  /** created | edited | status | rescheduled | attendee_added | attendee_removed | confirmation */
  kind: string;
  from_status: string | null;
  to_status: string | null;
  detail: string | null;
  by: string | null;
  created_at: string;
}

/** `GET /appointments/{id}`: what the details drawer shows. */
export interface AppointmentDetail extends Appointment {
  calendar_name: string;
  calendar_timezone: string;
  calendar_type: CalendarType;
  owner: AppointmentPerson | null;
  hosts: AppointmentPerson[];
  contact: AppointmentContact | null;
  /** A class session's contacts, in the order they joined, and its seats (ACL-293). */
  attendees: AppointmentContact[];
  seats: number | null;
  booked_by: AppointmentPerson | null;
  /** The appointment that replaced this one, when it was rescheduled. */
  rescheduled_to_id: number | null;
  /**
   * The confirmation text its contact was sent when it was booked, if the rep asked for one
   * (ACL-504). A class session's contacts have theirs on the history.
   */
  confirmation: AppointmentConfirmation | null;
  events: AppointmentEvent[];
}

/** Time blocked off (ACL-292): a person's (`user_id`) or a calendar's (`calendar_id`). */
export interface CalendarBlock {
  id: number;
  user_id: number | null;
  user_name: string | null;
  calendar_id: number | null;
  calendar_name: string | null;
  title: string | null;
  starts_at: string;
  ends_at: string;
  created_by_user_id: number | null;
  created_at: string;
}

/** What a create sends; an edit sends any part of it. */
export interface OpportunityListInput {
  name: string;
  view: "board" | "list";
  filters: { field: string; values: string[] }[];
  sort: { field: "created" | "name" | "stage" | "phone"; order: "asc" | "desc" };
  fields: string[];
  density: "comfortable" | "compact";
  shared: boolean;
}
